/**
 * Author: Claude Code
 * Version: v3.2.147
 * Modified: 2025-10-26 Beijing Time (v3.2.147: Resource sprites scale=50% tile; confirm roundPixels)
 *
 * BattleScene - Phaser3 Scene-centric架构重构
 * 集成WebSocket连接和事件处理到Scene生命周期中，移除GameManager中间层
 * v3.0.84: 新增Agent注册系统集成，支持配置化Agent初始化
 * v3.2.110: P2.1 Spectator System - Real-time spectator count display in top-right
 * v3.2.146: Scene handshake timing fix - Wait for game:loading:completed before sending ui:scene:ready
 */
import * as Phaser from 'phaser';
import { normalizeColor } from '../../utils/colorMapping';
import webSocketService from '../../services/websocket/WebSocketService';
// import { FrontendTestLogger, FrontendEventCategory } from '@utils/TestLogger'; // Commented out - using console.log for E2E testing
import { PhaserAnimationManager } from '../animation/PhaserAnimationManager';
import { PhaserCombatSequencer } from '../animation/PhaserCombatSequencer';
import { SpriteAssetLoader } from '../animation/SpriteAssetLoader';
import { CharacterType, WeaponType, AnimationType } from '../animation/spriteTypes';
import { SpriteAnimationPlayer } from '../animation/SpriteAnimationPlayer';
// Removed CombatAnimationEngine facade during cleanup; Sequencer is used directly
import { MovementAnimationSync } from '../animation/MovementAnimationSync';
// Authoritative Animation Engine (single-path architecture)
// Authoritative engine integration removed during cleanup phase

// 🆕 Frontend Systems Integration (v3.0.79+)
import { AgentSystem } from '../systems/AgentSystem';
import { ResourceSystem } from '../systems/ResourceSystem';
// 🔗 Single-path Animation Engine (new)
import SinglePathAnimationEngine from '../animation/engine/Engine';
import { SceneAdapter } from '../animation/engine/adapters/SceneAdapter';
import { CombatAdapter } from '../animation/engine/adapters/CombatAdapter';
import { EventTypes } from '../../../../backend/src/core/events/GameEventTypes';

// 🗺️ Map Configuration Integration (Phase 2.1)
import { 
  TerrainType, 
  MapSize, 
  MapGenerationOptions,
  DEFAULT_MAP_SIZE,
  MAX_MAP_SIZE,
  TILE_SIZE,
  TERRAIN_MOVEMENT_COSTS,
  TERRAIN_PASSABLE,
  DEFAULT_MAP_GENERATION_OPTIONS
} from '../map/config/MapConfigAdapter';

// 🌸 Seasonal Asset Integration (Phase 2.2)
// Removed SeasonalAssetManager usage for tilemap; we directly load from /assets/tilemap

// 🗺️ Dynamic Map Generation (Phase B.2)
import { 
  DynamicMapGenerator,
  MapGenerationResult 
} from '../map/generation/DynamicMapGenerator';

// 🎮 User Action Interfaces (Task 1.2.3)
interface UserAction {
  type: 'MOVE' | 'ATTACK' | 'INTERACT';
  timestamp: string;
  sequence: number;
  playerId: string;
  data: MoveActionData | AttackActionData | InteractActionData;
}

interface MoveActionData {
  direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
}

interface AttackActionData {
  targetX: number;
  targetY: number;
  button: number;
}

interface InteractActionData {
  targetX: number;
  targetY: number;
  button: number;
}
import { 
  AnyStateUpdate, 
  GameStateUpdate, 
  AgentStateUpdate, 
  ResourceStateUpdate, 
  SafeZoneStateUpdate,
  CombatStateUpdate,
  StateUpdateValidator,
  STATE_SYNC_CONFIG,
  Position,
  Health,
  GameContext,
  AgentState,
  ResourceState,
  SafeZoneState
} from '../../types/StateSyncProtocol';

/**
 * BattleScene - Engine-Centric Architecture (v3.0+)
 *
 * 🎯 UNIFIED ENTRY POINT: 只能通过 SinglePathAnimationEngine.handle() 控制所有动画
 * 🚨 CRITICAL: 所有动画行为必须通过 SinglePathAnimationEngine.handle() 路由
 * ❌ 禁止直接调用: phaserAnimationManager, phaserCombatSequencer, addAgent
 * ✅ 正确路由: this.animationEngine?.handle({ type: EventTypes.*, agentId, data })
 *
 * 🔒 ENGINE EXCLUSIVE ACCESS: Engine拥有动画系统的独占访问权
 * - phaserAnimationManager 和 phaserCombatSequencer 标记为 private
 * - 所有 @deprecated 的直接动画API仅用于向后兼容警告
 * - 新功能开发必须使用Engine统一入口，避免多路径并行
 *
 * Architecture Flow:
 * WebSocket → BattleScene (Router) → Engine (Priority Queue) → SceneAdapter → PhaserAnimationManager
 */
export class BattleScene extends Phaser.Scene {
  // UI configuration constants
  private static readonly UI_CONFIG = {
    HEALTH_BAR: {
      WIDTH: 40,
      HEIGHT: 4,
      Y_OFFSET: -25,
      COLORS: {
        HEALTHY: 0x00ff00,
        WOUNDED: 0xffff00,
        CRITICAL: 0xff0000,
        BACKGROUND: 0x000000
      },
      THRESHOLDS: {
        WOUNDED: 0.5,
        CRITICAL: 0.25
      }
    },
    NAME_TEXT: {
      Y_OFFSET: -30,
      FONT_SIZE: '12px',
      COLOR: '#ffffff'
    },
    SAFEZONE: {
      LINE_WIDTH: 2,
      CURRENT_COLOR: 0x00ff00,
      TARGET_COLOR: 0xff0000,
      CURRENT_ALPHA: 0.8,
      TARGET_ALPHA: 0.5
    },
    LLM_THINKING: {
      TINT_COLOR: 0x00ffff
    }
  } as const;

  
  // 🚫 WebSocket connection removed - now managed by WebSocketService
  private webSocketServiceInitialized: boolean = false;
  
  // State synchronization properties
  private lastSequenceNumber: number = 0;
  private sequenceBuffer: Map<number, AnyStateUpdate> = new Map();
  private _stateUpdateValidator: StateUpdateValidator | null = null;
  
  // Game objects
  private agents: Map<string, Phaser.GameObjects.Container> = new Map();
  private _terrain: Phaser.GameObjects.Group | null = null;
  private safeZone: Phaser.GameObjects.Graphics | null = null;
  private activeAgentHighlight: Phaser.GameObjects.Graphics | null = null;
  private heatmapBadge: Phaser.GameObjects.Container | null = null; // 小规模试验徽标
  private heatmapGraphics: Phaser.GameObjects.Graphics | null = null; // 热力图叠加层
  private gridSize: number = TILE_SIZE; // 48px - 统一tile和agent尺寸
  private gridGraphics: Phaser.GameObjects.Graphics | null = null; // 网格线叠加层
  private showGridOverlay: boolean = false; // 关闭网格（已完成校准）
  private sceneAssetsReady: boolean = false;
  private theaterAssetsReady: boolean = false;
  private awaitingServerSnapshot: boolean = true;
  private hasAppliedSnapshot: boolean = false;
  private knownAgents: Set<string> = new Set();
  private snapshotAgents: Set<string> = new Set();
  private snapshotResourceIds: Set<string> = new Set();
  private fallbackTimer: Phaser.Time.TimerEvent | null = null;
  
  // 🔥 SafeZone Animation Enhancement Properties
  private safeZoneWarningGraphics: Phaser.GameObjects.Graphics | null = null;
  private safeZoneTimer: Phaser.GameObjects.Text | null = null;
  private safeZoneAnimationTweens: Phaser.Tweens.Tween[] = [];
  private safeZoneState: string = 'normal';
  private safeZoneCallbacks = {
    onComplete: [] as Function[],
    onUpdate: [] as Function[]
  };

  // 👁️ P2.1: Spectator System UI
  private spectatorCountText: Phaser.GameObjects.Text | null = null;
  private currentSpectatorCount: number = 0;
  private mapWidth: number = 25; // 🆕 地图宽度（格子数）
  private mapHeight: number = 19; // 🆕 地图高度（格子数）
  private terrainGrid: Phaser.GameObjects.Text[][] = []; // 🆕 地形网格
  // 🆕 当前行动高亮箭头（agentId -> { gfx, tween }）
  private turnArrowMap: Map<string, { gfx: Phaser.GameObjects.Graphics; tween?: Phaser.Tweens.Tween }> = new Map();
  private awaitingHighlightAgentId: string | null = null;

  // ☁️ 云层动画系统
  private cloudLayer: Phaser.GameObjects.Group | null = null;
  private cloudSpawnTimer: Phaser.Time.TimerEvent | null = null;
  private activeClouds: Set<Phaser.GameObjects.Image> = new Set();
  private readonly MAX_CLOUDS = 4;
  private readonly CLOUD_DEPTH = 1700;

  // 🐉 飞龙动画系统
  private dragonLayer: Phaser.GameObjects.Group | null = null;
  private dragonSpawnTimer: Phaser.Time.TimerEvent | null = null;
  private activeDragons: Set<Phaser.GameObjects.Sprite> = new Set();
  private readonly MAX_DRAGONS = 2;
  private readonly DRAGON_DEPTH = 1700; // 与云朵同一图层
  // 🆕 后端权威地图（MAP_GENERATED）缓存
  private serverTerrainGrid: string[][] | null = null;
  
  // 🔥 新的Phaser3原生动画系统
  private phaserAnimationManager: PhaserAnimationManager | null = null;
  private phaserCombatSequencer: PhaserCombatSequencer | null = null;
  private combatEventRouter: any | null = null;
  private animationEngine: SinglePathAnimationEngine | null = null;
  private sceneAdapter: SceneAdapter | null = null;
  private combatAdapter: CombatAdapter | null = null;
  // In-world layered animation player for held-weapon animations outside theater
  private inWorldAssetLoader: SpriteAssetLoader | null = null;
  private inWorldAnimationPlayer: SpriteAnimationPlayer | null = null;
  // 🆕 Assets barrier: cache gameplay animation events until assetsReady
  private deferredGameplayEvents: Array<{ type: string; data: any }> = [];
  private processingDeferred: boolean = false;
  // 🔒 事件处理器去重与清理
  private handlersRegistered: boolean = false;
  private boundHandlers: { [key: string]: Function | null } = {
    GAME_STARTED: null,
    TURN_STARTED: null,
    GAME_ENDED: null,
    AGENT_SPAWNED: null,
    AGENT_MOVED: null,
    AGENT_ELIMINATED: null,
    COMBAT_STARTED: null,
    RESOURCE_SPAWNED: null,
    RESOURCE_COLLECTED: null,
    WEAPON_EQUIPPED: null,
    SAFEZONE_WARNING: null,
    SAFEZONE_CONTRACTION_STARTED: null,
    SAFEZONE_CONTRACTION_COMPLETED: null,
    SAFEZONE_UPDATED: null,
    SAFEZONE_DAMAGE_APPLIED: null,
    ENVIRONMENT_DAMAGE: null,
    ITEM_USED: null,
    ITEM_CONSUMED: null,
    COMBAT_DAMAGE_DEALT: null,
    COMBAT_ATTACK_MISSED: null,
    COMBAT_ENDED: null,
    SYSTEM_STATE_SYNC: null,
    MAP_GENERATED: null,
  };
  // HP闸门与缓存（剧场期间暂停地图HP刷新）
  private activeCombatCount: number = 0;
  private cachedHpUpdates: Map<string, { current: number; max: number }> = new Map();
  // ITEM_USED / ITEM_CONSUMED 去重：记录最近一次触发时间
  private lastItemUseTimestamp: Map<string, number> = new Map();
  private readonly ITEM_USE_DEDUP_MS = 2000;

  // 🆕 v3.2.139: Camera drag state tracking
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private readonly DRAG_THRESHOLD = 5; // pixels - minimum distance to consider as drag

  // Authoritative engine placeholders (will be reintroduced post-cleanup)
  // private authoritativeEngine: any | null = null;
  // private sceneRendererAdapter: any | null = null;
  // private combatRendererAdapter: any | null = null;
  
  // 🔥 Phase C: Resource Management System  
  private resourceContainer: Phaser.GameObjects.Group | null = null;
  
  // 🆕 Frontend Systems Integration (v3.0.79+)
  private agentSystem: AgentSystem | null = null;
  private resourceSystem: ResourceSystem | null = null;
  
  // 🌸 Seasonal Asset Manager (Phase 2.2)
  // Removed SeasonalAssetManager; direct asset keys are used
  // private seasonalAssetManager: SeasonalAssetManager;
  
  // 🗺️ Dynamic Map Generator (Phase B.2)
  private dynamicMapGenerator!: DynamicMapGenerator;
  private currentMapResult: MapGenerationResult | null = null;
  private mapSeed: number | undefined;
  
  // 🎮 User Input & Action System (Task 1.2.3)
  private keys: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key } | null = null;
  private actionSequence: number = 0;
  private playerId: string = '';
  private lastInputTime: number = 0;
  
  // Input system constants
  private static readonly INPUT_DEBOUNCE_DELAY = 100; // ms
  private static readonly DIRECTION_MAP = {
    'W': 'UP',
    'A': 'LEFT', 
    'S': 'DOWN',
    'D': 'RIGHT'
  } as const;
  private static readonly VALID_ACTION_TYPES = ['MOVE', 'ATTACK', 'INTERACT'] as const;

  constructor() {
    console.log('DEBUG: BattleScene constructor called');
    super({ key: 'BattleScene' });
    console.log('DEBUG: super() completed');
    // Generate unique player ID
    this.playerId = 'player_' + Math.random().toString(16).substr(2, 8);
    console.log('DEBUG: playerId generated:', this.playerId);
    
    // 🌸 Initialize seasonal asset manager (Phase 2.2)
    // SeasonalAssetManager removed
    
    // 🗺️ Initialize dynamic map generator (Phase B.2)
    try {
      this.dynamicMapGenerator = new DynamicMapGenerator();
      console.log('DEBUG: DynamicMapGenerator initialized');
    } catch (error) {
      console.warn('DynamicMapGenerator error:', error);
    }
    
    // 🆕 WebSocket service setup workaround
    // Since create() method execution has technical issues, initialize here as workaround
    this.webSocketServiceInitialized = false;
    
    console.log('DEBUG: BattleScene constructor completed');
  }

  // 🔥 Phase 1.1: WebSocket lifecycle management
  destroy(): void {
    console.log('BattleScene destroying - WebSocket cleanup now handled by WebSocketService');
    // Phaser3 Scene cleanup is handled automatically by shutdown lifecycle
  }

  // 🗺️ Phase B.2: Map seed configuration
  setMapSeed(seed: number): void {
    this.mapSeed = seed;
  }

  preload(): void {
    console.log('🎮 BattleScene: 加载游戏资源');
    
    // ✅ Phaser3最佳实践: 添加加载进度监听
    this.load.on('progress', (value: number) => {
      try { const agg = (require('../../services/progress/ProgressAggregator').default); agg.setClientProgress(Math.round(value * 100)); } catch {}
    });
    
    this.load.on('complete', () => {
      console.log('✅ 所有游戏资源加载完成');
      this.sceneAssetsReady = true;
      this.theaterAssetsReady = true;
      // 统一将所有纹理切换为最近邻采样，确保像素风清晰度
      try {
        const keys = Object.keys(this.textures.list);
        keys.forEach(k => {
          try { this.textures.get(k).setFilter(Phaser.Textures.FilterMode.NEAREST); } catch {}
        });
      } catch {}
      // Flush any gameplay events that arrived before assets were ready
      try { this.flushDeferredGameplayEvents(); } catch (e) { console.warn('flushDeferredGameplayEvents failed:', (e as Error).message); }
    });

    // 🔥 添加资源加载错误处理
    this.load.on('fileerror', (key: string, type: string, url: string) => {
      // 额外输出一条标准日志，便于测试页面的console代理捕获
      console.log(`🔎 FILEERROR key=${key} type=${type} url=${url}`);
      console.error(`❌ 资源加载失败: ${key} (${type}) - ${url}`);
      console.warn(`🔄 将为 ${key} 创建fallback纹理`);
      this.createFallbackTexture(key);
    });
    
    // 🔥 Phase 2.1: 统一走 SpriteAssetLoader 管道加载动画帧
    try {
      const spriteLoader = new SpriteAssetLoader(this);
      const allColors = [
        CharacterType.GREEN, CharacterType.RED, CharacterType.PURPLE,
        CharacterType.BLUE, CharacterType.WHITE, CharacterType.BLACK,
        CharacterType.ORANGE
      ];
      const allAnimations = [
        AnimationType.IDLE, AnimationType.ATTACK, AnimationType.HURT,
        AnimationType.DIE, AnimationType.RUSH, AnimationType.USE,
        AnimationType.MOVE
      ];
      allColors.forEach(color => {
        allAnimations.forEach(anim => spriteLoader.loadCharacterAnimation(color, anim));
      });
      const weaponAnims: Array<[WeaponType, AnimationType]> = [
        [WeaponType.SWORD, AnimationType.IDLE],
        [WeaponType.AXE, AnimationType.IDLE],
        [WeaponType.LANCE, AnimationType.IDLE],
        [WeaponType.SWORD, AnimationType.MOVE],
        [WeaponType.AXE, AnimationType.MOVE],
        [WeaponType.LANCE, AnimationType.MOVE],
        [WeaponType.SWORD, AnimationType.ATTACK],
        [WeaponType.AXE, AnimationType.ATTACK],
        [WeaponType.LANCE, AnimationType.ATTACK],
        [WeaponType.SWORD, AnimationType.HURT],
        [WeaponType.AXE, AnimationType.HURT],
        [WeaponType.LANCE, AnimationType.HURT],
        [WeaponType.SWORD, AnimationType.DIE],
        [WeaponType.AXE, AnimationType.DIE],
        [WeaponType.LANCE, AnimationType.DIE]
      ];
      weaponAnims.forEach(([w, a]) => spriteLoader.loadWeaponAnimation(w, a));
    } catch {}
    this.loadWeaponAssets();
    this.loadResourceAssets();
    
    // 🔥 Phase B.1: 季节性地图素材加载 (替代占位符)
    this.loadTilemapAssets();
  }

  // testMethod removed (legacy experimental hook)

  create(data?: any): void {
    console.log('🎮 BattleScene: create()方法被调用 - 开始初始化游戏世界');

    // Reset snapshot state for new scene lifecycle
    this.awaitingServerSnapshot = true;
    this.hasAppliedSnapshot = false;
    this.knownAgents.clear();
    this.snapshotAgents.clear();
    this.snapshotResourceIds.clear();

    if (data?.roomId) {
      this.registry.set('roomId', data.roomId);
    } else if (!this.registry.get('roomId')) {
      try {
        const sharedRoomId = (this.game as any)?.registry?.get?.('roomId');
        if (sharedRoomId) {
          this.registry.set('roomId', sharedRoomId);
        }
      } catch {}
    }

    // 🆕 v3.0.84: Fixed system initialization - call full initialization method
    if (!this.webSocketServiceInitialized) {
      this.initializeWebSocketService();
      this.webSocketServiceInitialized = true;
    }
    // 将 tile/grid 尺寸写入 Registry，便于其他系统（如 ResourceSystem）读取
    try {
      this.registry.set('gridSize', this.gridSize);
      this.registry.set('tileSize', this.gridSize);
      // 相机自动跟随默认开启，可由 UI 开关关闭
      if (typeof this.registry.get('autoFollowCamera') === 'undefined') {
        this.registry.set('autoFollowCamera', true);
      }
    } catch {}
    
    // 初始化游戏世界和摄像机
    this.initializeGameWorld();
    try { this.cameras.main.roundPixels = true; } catch {}
    // 保持全局动画时间刻度一致，避免个别场景或插件修改导致快慢不一
    try { this.anims.globalTimeScale = 1; } catch {}
    
    // 🆕 添加地图生成调用 - 确保地图和UI正确显示
    // NOTE: 初始化阶段不要提前调用，避免后续再次调用导致残留图层
    // 初次地图渲染放在系统与容器初始化之后（见下文第二次调用）
    // this.initializeGridMap();
    
    // 🆕 v3.0.84: Handle registered agents from GameManager
    this.handleRegisteredAgents(data);

    console.log('✅ BattleScene: create()方法完成 - 游戏世界初始化完毕');

    // Test logging: Track scene initialization
    // FrontendTestLogger.log(FrontendEventCategory.SCENE, 'SCENE_INITIALIZED', {
    //   roomId: this.registry.get('roomId'),
    //   hasWebSocket: this.webSocketServiceInitialized
    // }); // Commented out - using console.log for E2E testing

    // 移除本地地图fallback：严格依赖后端权威快照（system:stateSync/MAP_GENERATED）

    // 监听后端热力图标记事件（绘制半透明热力图单元格）
    try {
      const onHeatmapMark = ((e: any) => {
        try {
          const cells = Array.isArray(e?.detail?.cells) ? e.detail.cells : [];
          this.drawHeatmap(cells);
        } catch (err) {
          console.warn('BattleScene: heatmap draw failed:', (err as Error).message);
        }
      }) as any;
      // 存在多次create的情况，仅注册一次
      (this as any)._hmListener && window.removeEventListener('ABR_UI_HEATMAP_MARK', (this as any)._hmListener);
      (this as any)._hmListener = onHeatmapMark;
      window.addEventListener('ABR_UI_HEATMAP_MARK', onHeatmapMark);
    } catch {}

    // 触发create事件，通知外部监听者
    this.events.emit('create');

    // 🆕 v3.2.146: Scene Handshake Protocol - 等待game:loading:completed后再发送握手
    // ✅ 所有监听器已绑定完成(setupGameEventHandlers)
    // ⏳ 握手将在handleGameLoadingCompleted()中自动触发(事件桥ready后)
    console.log('✅ BattleScene: Listeners setup complete, waiting for game:loading:completed...');

    // 保障：若本Scene在loading完成之后才创建（常见于LoadingScene→BattleScene切换），
    // 直接发送一次scene:ready握手，避免错过GAME_LOADING_COMPLETED事件。
    // 后续若仍收到GAME_LOADING_COMPLETED，sendSceneReadyHandshake会因去重标记而不重复发送。
    this.time.delayedCall(0, () => this.sendSceneReadyHandshake());
    // 兜底：3.5秒后仍未拿到快照，则强制重发一次握手
    this.time.delayedCall(3500, () => {
      try {
        const channelOpen = !!this.registry.get('channelOpen');
        if (!this.hasAppliedSnapshot && !channelOpen) {
          console.warn('⏳ Snapshot not received within 3.5s, retrying scene:ready');
          this.sendSceneReadyHandshake(true);
        }
      } catch (e) {
        console.warn('Handshake retry check failed:', (e as Error).message);
      }
    });
    // 二次兜底：5s 后仍未拿到快照，则直接请求一次快照
    this.time.delayedCall(5000, () => {
      try {
        if (!this.hasAppliedSnapshot) {
          const rid = this.registry.get('roomId');
          if (rid) {
            console.warn('🛰️ Requesting snapshot due to delayed reception');
            webSocketService.requestSnapshot(rid);
          }
        }
      } catch (e) { console.warn('requestSnapshot fallback failed:', (e as Error).message); }
    });
  }

  // 初始化游戏世界
  private initializeGameWorld(): void {
    console.log('🌍 初始化游戏世界...');

    // 设置摄像机边界 - 50x50 tiles * 24px = 1200x1200
    this.cameras.main.setBounds(0, 0, 1200, 1200);

    // 设置世界边界
    this.physics.world.setBounds(0, 0, 1200, 1200);

    // 设置摄像机背景色
    this.cameras.main.setBackgroundColor('#2c3e50');

    // 👁️ P2.1: Create spectator count text in top-right corner
    this.spectatorCountText = this.add.text(
      this.cameras.main.width - 150,
      20, // 20px from top, slightly above safe zone timer (50px)
      '👁️ Spectators: 0',
      {
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#000000',
        padding: { x: 8, y: 4 }
      }
    ).setScrollFactor(0).setDepth(1000);

    console.log('✅ 游戏世界初始化完成 - 边界设为1200x1200 (50x50 * 24px)');
  }

  // 🆕 Handle registered agents initialization
  private handleRegisteredAgents(sceneData?: any): void {
    try {
      // Check for agents in scene data
      const agents = sceneData?.agents || this.data?.get('registeredAgents');
      
      if (agents && Array.isArray(agents)) {
        console.log(`Initializing ${agents.length} registered agents`);
        
        // Initialize each agent in the scene
        agents.forEach((agentRegistration: any) => {
          if (agentRegistration.spawnPosition && agentRegistration.configuration) {
            this.initializeRegisteredAgent(
              agentRegistration.id,
              agentRegistration.username,
              agentRegistration.configuration,
              agentRegistration.spawnPosition
            );
          }
        });
        
        // Store agent data in registry for other systems
        this.registry.set('registeredAgents', agents);
        console.log('Registered agents initialized successfully');
      } else {
        console.log('No registered agents found, proceeding with empty battle scene');
      }
    } catch (error) {
      console.error('Error handling registered agents:', error);
    }
  }

  // Initialize a single registered agent
  private initializeRegisteredAgent(
    agentId: string,
    username: string,
    configuration: any,
    spawnPosition: { x: number; y: number }
  ): void {
    try {
      // Ensure AgentSystem is initialized
      if (!this.agentSystem) {
        this.agentSystem = new AgentSystem(this);
      }

      // Create agent data with configuration
      const agentData = {
        id: agentId,
        position: spawnPosition,
        health: configuration.hp,
        maxHealth: configuration.hp,
        color: this.getAgentColorFromId(agentId),
        // Store full configuration for later use
        configuration: configuration,
        username: username
      };

      // Spawn the agent in the scene
      this.agentSystem.spawnAgent(agentData);

      console.log(`Agent ${username} (${agentId}) spawned at (${spawnPosition.x}, ${spawnPosition.y})`);
      console.log('Agent configuration:', configuration);
      
    } catch (error) {
      console.error(`Error initializing agent ${agentId}:`, error);
    }
  }

  // Get agent color based on agent ID (for visual distinction)
  private getAgentColorFromId(agentId: string): string {
    const colors = ['red', 'blue', 'green', 'yellow', 'purple'];
    const index = agentId.split('_')[1]?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  }

  // 兼容保留：移除旧的 Workaround 版本，统一走 initializeWebSocketService，避免重复注册

  private initializeWebSocketService(): void {
    // 🆕 WebSocketService集成 - 设置Registry引用
    webSocketService.setGameRegistry(this.registry);
    
    // 🆕 设置Registry状态变化监听
    this.registry.events.on('changedata', this.handleRegistryChange, this);
    
    // 🆕 设置WebSocket游戏事件监听
    this.setupGameEventHandlers();
    
    // 动画系统由PhaserAnimationManager管理
    
    // 🚫 Phase 1.1: WebSocket connection now managed by WebSocketService
    // this.initializeWebSocketConnection(); // REMOVED: Direct WebSocket initialization
    
    // Initialize animation sequencer
    try {
      // Initialize PhaserAnimationManager (Phaser3 native implementation)
      this.phaserAnimationManager = new PhaserAnimationManager(this);
      
      // Initialize Sequencer (theater combat system)
      this.phaserCombatSequencer = new PhaserCombatSequencer(this);
      // Initialize in-world layered animation player
      try {
        this.inWorldAssetLoader = new SpriteAssetLoader(this);
        this.inWorldAnimationPlayer = new SpriteAnimationPlayer(this, this.inWorldAssetLoader);
      } catch {}
      // Initialize single-path animation engine and adapters
      this.initializeSinglePathAnimationEngine();

      // Authoritative engine wiring skipped during cleanup

      // 🔧 旧式 create() 动态加载与下划线命名预载已移除，统一依赖 preload 阶段的 SpriteAssetLoader

      // Dev/test: listen for a one-off layered move test event
      try {
        window.addEventListener('ABR_TEST_PLAY_LAYERED_MOVE', (ev: any) => {
          try {
            const detail = ev?.detail || {};
            const agentId = String(detail.agentId || 'sa_agent1');
            const weapon = String(detail.weaponType || 'SWORD').toUpperCase();
            const color = (this.phaserAnimationManager as any)?.agentColors?.get(agentId) || 'green';
            const container = this.phaserAnimationManager?.agentContainers.get(agentId);
            if (!this.inWorldAnimationPlayer || !container) { console.warn('ABR_TEST_PLAY_LAYERED_MOVE: missing player/container'); return; }
            const charType = this.mapColorToCharacterType(String(color));
            const weapType = this.mapWeaponString(weapon);
            // Play a layered MOVE animation once under the agent container
            this.inWorldAnimationPlayer.playLayeredAnimation(
              `${agentId}#test-move`,
              charType,
              weapType,
              AnimationType.MOVE,
              { x: 0, y: 0 },
              { loop: false, fps: 12, parentContainer: container, scale: 1.2, onComplete: () => {
                try { this.inWorldAnimationPlayer?.stopAnimation(`${agentId}#test-move`); } catch {}
              } }
            );
          } catch (e) {
            console.warn('ABR_TEST_PLAY_LAYERED_MOVE failed:', (e as Error).message);
          }
        });
      } catch {}
    } catch (error) {
      console.warn('Animation managers not available in test environment:', (error as Error).message);
    }
    
    // Initialize terrain group
    this._terrain = this.add.group();
    
    // 🔥 Phase C: Initialize resource container
    this.resourceContainer = this.add.group();
    
    // 🆕 Initialize Frontend Systems (v3.0.79+)
    try {
      this.agentSystem = new AgentSystem(this);
      this.resourceSystem = new ResourceSystem(this);
    } catch (error) {
      console.warn('Frontend systems initialization error:', error);
    }
    
    console.log('✨ Frontend systems initialized: AgentSystem + ResourceSystem');
    
    // Initialize safe zone graphics
    this.safeZone = this.add.graphics();
    // Raise depth to ensure visibility above terrain/overlays and beneath agents
    this.safeZone.setDepth(1400);
    this.safeZone.setVisible(true);
    
    // 地图渲染改为在 GAME_STARTED 后触发，避免页面加载即生成地图
    
    // Add UI overlay
    this.add.text(10, 10, 'Agent Battle Royale 3.0 - Grid Map', {
      fontSize: '16px',
      color: '#ffffff'
    }).setScrollFactor(0); // 固定在屏幕上，不跟随摄像头

    this.add.text(10, 30, `地图: ${this.mapWidth}x${this.mapHeight} 网格`, {
      fontSize: '12px',
      color: '#00ff00'
    }).setScrollFactor(0);

    // 🆕 添加操作提示
    this.add.text(10, 50, '🖱️ 拖拽移动地图 | 🔍 滚轮缩放', {
      fontSize: '12px',
      color: '#ffff00'
    }).setScrollFactor(0);
    
    // 🔥 Phase 1.2.3: Setup input handlers for user actions
    this.setupInputHandlers();
  }

  /**
   * Initialize Single-Path Animation Engine with Scene/Combat adapters
   */
  private initializeSinglePathAnimationEngine(): void {
    try {
      if (!this.phaserAnimationManager || !this.phaserCombatSequencer) return;
      this.animationEngine = new SinglePathAnimationEngine();
      this.sceneAdapter = new SceneAdapter(this, this.phaserAnimationManager);
      this.combatAdapter = new CombatAdapter(this, this.phaserCombatSequencer);
      this.animationEngine.setAdapters(this.sceneAdapter, this.combatAdapter);
      // Assets are preloaded by BattleScene; mark ready
      this.animationEngine.setReady({ scene: this.sceneAssetsReady, theater: this.theaterAssetsReady });
      console.log('✅ Single-Path Animation Engine initialized');
    } catch (e) {
      console.warn('Single-Path Animation Engine init failed:', e);
    }
  }

  // private initializeAuthoritativeAnimationEngine(): void {}

  update(_time: number, _delta: number): void {
    // Check keyboard input each frame
    if (this.keys) {
      if (this.keys.W.isDown) this.handleKeyboardInput('W');
      else if (this.keys.A.isDown) this.handleKeyboardInput('A');
      else if (this.keys.S.isDown) this.handleKeyboardInput('S');
      else if (this.keys.D.isDown) this.handleKeyboardInput('D');
    }
  }

  // Methods for managing game objects
  /**
   * @deprecated 禁止直接创建Agent，请通过Engine.handle(AGENT_SPAWNED)路由到SceneAdapter
   */
  addAgent(id: string, gridX: number, gridY: number): any {
    console.warn('🚨 Deprecated API: addAgent - 请通过Engine.handle(AGENT_SPAWNED)事件创建Agent');
    if (!this.phaserAnimationManager) {
      console.error('❌ PhaserAnimationManager not initialized');
      return;
    }

    // 转换网格坐标到像素坐标 (handled by PhaserAnimationManager)

    // 🔥 使用新的Phaser3原生动画系统创建Agent
    const agentContainer = this.phaserAnimationManager.addAgent(id, gridX, gridY);
    
    if (agentContainer) {
      // 获取实际的Container对象来添加UI元素
      const container = this.phaserAnimationManager.agentContainers.get(id);
      if (container) {
        // 添加Agent ID标签
        const agentText = this.add.text(0, -35, id, {
          fontSize: '8px',
          color: '#ffffff',
          align: 'center',
          stroke: '#000000',
          strokeThickness: 1
        }).setOrigin(0.5, 0.5);
        
        container.add(agentText);
        this.agents.set(id, container);
      }
      
      console.log(`✅ Agent ${id} 已添加Phaser3动画 at grid(${gridX}, ${gridY})`);
    } else {
      console.error(`❌ 创建Agent ${id} Phaser3动画失败`);
    }
    
    return agentContainer;
  }

  removeAgent(id: string): void {
    const agent = this.agents.get(id);
    if (agent) {
      agent.destroy();
      this.agents.delete(id);
      console.log(`🗑️ Agent ${id} 已从Phaser3动画系统中移除`);
    }
  }

  updateAgentPosition(id: string, gridX: number, gridY: number): void {
    if (!this.phaserAnimationManager) {
      console.warn('⚠️ PhaserAnimationManager not available');
      return;
    }

    console.log(`🎯 Agent ${id} 开始同步移动 to grid(${gridX}, ${gridY})`);
    // 边界裁剪，防止越界
    const clampedX = Math.max(0, Math.min(this.mapWidth - 1, gridX));
    const clampedY = Math.max(0, Math.min(this.mapHeight - 1, gridY));

    // 🔧 修复：统一使用容器移动系统，避免双重更新冲突
    const success = this.phaserAnimationManager.moveAgentWithAnimation(
      id,
      clampedX,
      clampedY,
      () => {
        // 移动完成回调
        console.log(`✅ Agent ${id} 容器同步移动完成 to grid(${clampedX}, ${clampedY})`);
        try {
          window.dispatchEvent(new CustomEvent('AGENT_MOVE_ANIM_DONE', { detail: { agentId: id, x: clampedX, y: clampedY } }));
        } catch {}
      }
    );

    if (!success) {
      // 如果同步移动失败（比如Agent正在移动中），只打印警告，不再fallback
      // 避免双重位置更新导致的不同步问题
      console.warn(`⚠️ Agent ${id} 正在移动中，跳过此次移动请求`);
    }
  }

  updateSafeZone(centerGridX: number, centerGridY: number, radiusInTiles: number): void {
    if (!this.safeZone) {
      this.safeZone = this.add.graphics();
      this.safeZone.setDepth(1400);
      this.safeZone.setVisible(true);
    }
    if (this.safeZone) {
      const centerPixel = this.getPixelPosition(centerGridX, centerGridY);
      const radiusPixels = radiusInTiles * this.gridSize;
      
      // Ensure SafeZone on top of terrain, under agents
      this.safeZone.setDepth(1400);
      this.safeZone.setVisible(true);
      
      this.safeZone.clear();
      this.safeZone.lineStyle(3, 0x00ff00, 0.9);
      this.safeZone.strokeCircle(centerPixel.x, centerPixel.y, radiusPixels);
      // 🔧 修复：移除安全区内部填充，只保留边框线条
      // this.safeZone.fillStyle(0x00ff00, 0.05);
      // this.safeZone.fillCircle(centerPixel.x, centerPixel.y, radiusPixels);
      
      console.log(`Safe zone updated: center grid(${centerGridX}, ${centerGridY}), radius ${radiusInTiles} tiles, depth: ${this.safeZone.depth}`);
    }
  }

  // 🆕 高亮当前活跃的agent（正在思考的agent）
  highlightActiveAgent(agentId: string): void {
    const agent = this.agents.get(agentId);
    if (!agent) {
      console.warn(`BattleScene: 无法高亮agent ${agentId}，agent不存在`);
      return;
    }

    // 创建或更新高亮效果
    if (!this.activeAgentHighlight) {
      this.activeAgentHighlight = this.add.graphics();
    }

    // 清除之前的高亮
    this.activeAgentHighlight.clear();

    // 获取agent位置
    const agentX = agent.x;
    const agentY = agent.y;

    // 绘制思考指示器 - 脉冲光环
    this.activeAgentHighlight.lineStyle(3, 0x00ffff, 0.8); // 青色光环
    this.activeAgentHighlight.strokeCircle(agentX, agentY, 30);
    
    // 添加思考文字
    if (agent.list && agent.list.length > 2) {
      // 移除旧的思考文字（如果存在）
      const existingThinkingText = agent.list.find((child: any) => child.getData && child.getData('isThinkingText'));
      if (existingThinkingText) {
        agent.remove(existingThinkingText);
        existingThinkingText.destroy();
      }
    }

    // 添加新的思考文字
    const thinkingText = this.add.text(0, -50, '🤖 Thinking...', {
      fontSize: '12px',
      color: '#00ffff',
      align: 'center'
    }).setOrigin(0.5, 0.5);
    
    thinkingText.setData('isThinkingText', true);
    agent.add(thinkingText);

    console.log(`BattleScene: Agent ${agentId} 已高亮，显示思考状态`);

    // 添加脉冲动画
    this.tweens.add({
      targets: this.activeAgentHighlight,
      alpha: { from: 1, to: 0.3 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  // 🆕 清除agent高亮
  clearActiveHighlight(): void {
    if (this.activeAgentHighlight) {
      this.activeAgentHighlight.clear();
      this.tweens.killTweensOf(this.activeAgentHighlight);
    }

    // 清除所有思考文字
    this.agents.forEach(agent => {
      if (agent.list) {
        const thinkingTexts = agent.list.filter((child: any) => child.getData && child.getData('isThinkingText'));
        thinkingTexts.forEach((text: any) => {
          agent.remove(text);
          text.destroy();
        });
      }
    });

    console.log('BattleScene: 已清除所有agent高亮');
  }

  // 小规模试验：在指定agent容器上方绘制一个“Heatmap ✓”徽标
  private showHeatmapBadge(agentId: string): void {
    try {
      // 获取容器
      let container: Phaser.GameObjects.Container | undefined;
      try { container = this.phaserAnimationManager?.agentContainers.get(agentId); } catch {}
      if (!container) { container = this.agents.get(agentId); }
      if (!container) {
        console.warn(`BattleScene: heatmap badge skipped, agent container not found for ${agentId}`);
        return;
      }

      // 清除旧徽标
      if (this.heatmapBadge) { this.heatmapBadge.destroy(); this.heatmapBadge = null; }

      // 在全局场景坐标绘制徽标
      const badge = this.add.container(container.x, container.y - 42);
      const bg = this.add.rectangle(0, 0, 64, 20, 0x10b981, 0.95).setOrigin(0.5, 0.5);
      bg.setStrokeStyle(1, 0x083a2a, 1);
      const txt = this.add.text(0, 0, 'Heatmap ✓', { fontSize: '12px', color: '#ffffff' }).setOrigin(0.5, 0.5);
      badge.add([bg, txt]);
      badge.setDepth(1600);

      // 轻微浮动动效 + 自动淡出
      this.tweens.add({ targets: badge, y: badge.y - 6, duration: 300, yoyo: true, repeat: 2, ease: 'Sine.easeInOut' });
      this.tweens.add({ targets: badge, alpha: { from: 1, to: 0 }, delay: 1200, duration: 600, onComplete: () => { try { badge.destroy(); } catch {} } });

      this.heatmapBadge = badge;
      console.log(`BattleScene: heatmap badge shown for ${agentId}`);
    } catch (e) {
      console.warn('BattleScene: showHeatmapBadge error', e as any);
    }
  }

  // 在Phaser场景内绘制热力图（Top-N分数格覆盖，半透明单元格）
  private drawHeatmap(cells: Array<{ x:number; y:number; score:number }>): void {
    try {
      // 清理旧图层
      if (this.heatmapGraphics) { this.heatmapGraphics.clear(); this.heatmapGraphics.destroy(); this.heatmapGraphics = null; }
      if (!cells || cells.length === 0) return;
      if (!this.mapWidth || !this.mapHeight || !this.gridSize) return;

      const g = this.add.graphics();
      g.setDepth(1500); // 高于地形、低于高亮

      // 归一化分数到透明度/颜色强度
      const maxScore = Math.max(...cells.map(c => typeof c.score === 'number' ? c.score : 0), 1);
      for (const c of cells) {
        if (c.x < 0 || c.y < 0 || c.x >= this.mapWidth || c.y >= this.mapHeight) continue;
        const px = c.x * this.gridSize;
        const py = c.y * this.gridSize;
        const intensity = Math.max(0.15, Math.min(1, (c.score || 0) / maxScore));
        const r = Math.floor(255 * intensity);
        const gcol = Math.floor(80 * intensity);
        const b = 0;
        const color = (r << 16) | (gcol << 8) | b;
        g.fillStyle(color, 0.35);
        g.fillRect(px, py, this.gridSize, this.gridSize);
        g.lineStyle(1, 0xffffff, 0.15);
        g.strokeRect(px, py, this.gridSize, this.gridSize);
      }
      this.heatmapGraphics = g;
      console.log(`BattleScene: heatmap drawn for ${cells.length} cell(s)`);
    } catch (e) {
      console.warn('BattleScene: drawHeatmap error', e as any);
    }
  }


  // 🔥 获取动画系统调试信息 (INTEGRATION: 包含PhaserCombatSequencer)
  /**
   * @deprecated 禁止直接访问动画调试信息，请通过Engine获取统一调试接口
   */
  getAnimationDebugInfo(): any {
    console.warn('🚨 Deprecated API: getAnimationDebugInfo - 请通过Engine获取统一调试信息');
    const phaserInfo = this.phaserAnimationManager?.getAnimationDebugInfo();
    const phaserCombatInfo = this.phaserCombatSequencer?.getDebugInfo();

    return {
      phaserAnimationManager: phaserInfo,
      phaserCombatSequencer: phaserCombatInfo,
      sceneAgents: this.agents.size,
      integrationStatus: 'PHASER_ACTIVE'
    };
  }


  private initializeGridMap(): void {
    // 🔍 DEBUG: Map initialization start
    console.log(`🗺️ ========================================`);
    console.log(`🏁 [BattleScene] initializeGridMap() STARTED`);
    console.log(`   serverTerrainGrid exists:`, !!this.serverTerrainGrid);
    console.log(`   serverTerrainGrid length:`, this.serverTerrainGrid?.length || 0);
    console.log(`🗺️ ========================================`);

    // v3.2.140: 100% Backend Map Authority - No local fallback
    const useServer = Array.isArray(this.serverTerrainGrid) && this.serverTerrainGrid!.length > 0;

    if (!useServer) {
      console.warn('🚫 Backend map not yet available - waiting for authority snapshot');
      console.info('📍 Expect system:stateSync or MAP_GENERATED after ui:scene:ready handshake');
      return; // Never generate local fallback map
    } else {
      console.log(`✅ [BattleScene] Using backend-generated map`);
      console.log(`   Map dimensions: ${this.serverTerrainGrid![0].length}x${this.serverTerrainGrid!.length}`);
      // 将后端 TerrainType[][] 映射到前端渲染层（base/overlay）
      console.log(`🗺️ 渲染后端权威地图 (backend-generated map)`);
      const grid = this.serverTerrainGrid!;
      const H = grid.length; const W = grid[0].length;
      const base: ('PLAIN'|'WATER'|'SAND')[][] = Array.from({length:H},()=>Array(W).fill('PLAIN'));
      const overlay: ('NONE'|'FOREST01'|'FOREST02'|'MOUNTAIN'|'BUILDING01'|'BUILDING02'|'FENCE')[][] =
        Array.from({length:H},()=>Array(W).fill('NONE'));
      const rnd = () => Math.random();
      for (let y=0;y<H;y++) for (let x=0;x<W;x++) {
        const t = String(grid[y][x]||'PLAIN').toUpperCase();
        if (t === 'WATER') { base[y][x] = 'WATER'; overlay[y][x] = 'NONE'; continue; }
        base[y][x] = 'PLAIN';
        if (t === 'FOREST') overlay[y][x] = rnd() < 0.5 ? 'FOREST01' : 'FOREST02';
        else if (t === 'MOUNTAIN') overlay[y][x] = 'MOUNTAIN';
        else if (t === 'BUILDING') overlay[y][x] = rnd() < 0.5 ? 'BUILDING01' : 'BUILDING02';
        else overlay[y][x] = 'NONE';
      }
      // Compute simple metrics for display consistency
      const total = W * H;
      let water = 0, sand = 0, forest01 = 0, forest02 = 0, mountain = 0, building01 = 0, building02 = 0, fence = 0;
      for (let y=0;y<H;y++) for (let x=0;x<W;x++) {
        if (base[y][x] === 'WATER') water++;
        else if (base[y][x] === 'SAND') sand++;
        const o = overlay[y][x];
        if (o === 'FOREST01') forest01++;
        else if (o === 'FOREST02') forest02++;
        else if (o === 'MOUNTAIN') mountain++;
        else if (o === 'BUILDING01') building01++;
        else if (o === 'BUILDING02') building02++;
        else if (o === 'FENCE') fence++;
      }
      const metrics = {
        waterPct: total ? water / total : 0,
        sandPct: total ? sand / total : 0,
        forest01Pct: total ? forest01 / total : 0,
        forest02Pct: total ? forest02 / total : 0,
        mountainPct: total ? mountain / total : 0,
        building01Pct: total ? building01 / total : 0,
        building02Pct: total ? building02 / total : 0,
        fencePct: total ? fence / total : 0,
        passableLargestRatio: 1
      };
      this.currentMapResult = {
        baseLayer: base,
        overlayLayer: overlay,
        seed: Date.now(),
        connectivity: { passableRatio: 1, largestComponentRatio: 1 },
        phaserData: { baseTileIndices: [], overlayTileIndices: [], tilesetMapping: { base:{}, overlay:{} } },
        metrics
      } as any;
    }
    
    // 更新地图尺寸（防御：确保层存在且非空）
    const baseLayer = this.currentMapResult && (this.currentMapResult as any).baseLayer;
    if (!baseLayer || !Array.isArray(baseLayer) || baseLayer.length === 0 || !Array.isArray(baseLayer[0])) {
      console.warn('BattleScene: baseLayer missing or empty; abort rendering');
      return;
    }
    this.mapHeight = baseLayer.length;
    this.mapWidth = baseLayer[0].length;
    
    console.log(`🎯 动态地图生成完成: ${this.mapWidth}x${this.mapHeight}, 连通性: ${(this.currentMapResult.connectivity.passableRatio * 100).toFixed(1)}%`);

    // 清空现有地形图像（防止重复生成导致的图层残留与覆盖错位）
    if (this._terrain) {
      // true, true => remove and destroy children
      this._terrain.clear(true, true);
    }
    this.terrainGrid = [];
    
    // 自动拼接准备
    const waterMask = baseLayer.map((row: any[]) => row.map(c => (c === 'WATER' ? 1 : 0)));
    const sandMask = baseLayer.map((row: any[]) => row.map(c => (c === 'SAND' ? 1 : 0)));
    const waterTiles = this.generateAutoTileMap('WATER', waterMask);
    const sandTiles = this.generateAutoTileMap('SAND', sandMask);

    // 🌊 应用海洋外圈强制贴图（匹配water-terrain-generator.html的applyOceanRingTiles逻辑）
    this.applyOceanRingTiles(waterTiles, waterMask);
    
    // 🔥 核心实现：双层地图渲染 (Base层 + Overlay层)
    for (let y = 0; y < this.mapHeight; y++) {
      const row: Phaser.GameObjects.Text[] = []; // TODO: 改为Image数组
      for (let x = 0; x < this.mapWidth; x++) {
        const pixelX = x * this.gridSize + this.gridSize / 2;
        const pixelY = y * this.gridSize + this.gridSize / 2;
        
        // 🌍 Base层渲染 (PLAIN/WATER)
        const baseType = baseLayer[y][x];
        let baseAssetKey: string;
        if (baseType === 'WATER') baseAssetKey = waterTiles[y][x];
        else if (baseType === 'SAND') baseAssetKey = sandTiles[y][x];
        else baseAssetKey = 'PLAIN';
        const baseImage = this.add.image(pixelX, pixelY, baseAssetKey);
        baseImage.setOrigin(0.5, 0.5);
        baseImage.setDepth(1); // Base层深度为1
        baseImage.setDisplaySize(this.gridSize, this.gridSize);
        if (this._terrain) this._terrain.add(baseImage);
        
        // 🏔️ Overlay层渲染 (FOREST/MOUNTAIN/BUILDING/NONE) - 仅在PLAIN地形上渲染
        const overlayLayer = (this.currentMapResult as any).overlayLayer || [];
        const overlayType = overlayLayer[y]?.[x] || 'NONE';
        if (baseType === 'PLAIN' && overlayType !== 'NONE') {
          const overlayAssetKey = this.getOverlayAssetKey(overlayType);
          const overlayImage = this.add.image(pixelX, pixelY, overlayAssetKey);
          overlayImage.setOrigin(0.5, 0.5);
          overlayImage.setDepth(2); // Overlay层深度为2，在Base层之上
          overlayImage.setDisplaySize(this.gridSize, this.gridSize);
          if (this._terrain) this._terrain.add(overlayImage);
        }

        // 📍 在每个tile中央绘制坐标（浅灰色），便于定位
        // ⚠️ 临时注释：正式演示时关闭坐标显示，需要调试时可重新启用
        /*
        try {
          const coordText = this.add.text(pixelX, pixelY, `${x},${y}`, {
            fontFamily: 'monospace',
            fontSize: Math.max(10, Math.floor(this.gridSize * 0.22)).toString() + 'px',
            color: '#bbbbbb'
          });
          coordText.setOrigin(0.5, 0.5);
          coordText.setDepth(3);
          if (this._terrain) this._terrain.add(coordText);
        } catch {}
        */

        // 保持兼容性（暂时还是用text数组，后续重构为image数组）
        row.push(baseImage as any);
      }
      this.terrainGrid.push(row);
    }
    
    console.log(`🎨 双层地图渲染完成: Base层+Overlay层 = ${this.mapWidth * this.mapHeight}个基础格子`);
    console.log(`📊 地形分布 - 水域: ${(this.currentMapResult.metrics.waterPct * 100).toFixed(1)}%, 山地: ${(this.currentMapResult.metrics.mountainPct * 100).toFixed(1)}%, 森林: ${((this.currentMapResult.metrics.forest01Pct + this.currentMapResult.metrics.forest02Pct) * 100).toFixed(1)}%, 建筑: ${((this.currentMapResult.metrics.building01Pct + this.currentMapResult.metrics.building02Pct) * 100).toFixed(1)}%`);

    // 🧭 绘制网格线用于位置对齐检查
    this.drawGridOverlay();

    // 地图生成后，设置摄像头与物理边界
    const worldWidth = this.mapWidth * this.gridSize;
    const worldHeight = this.mapHeight * this.gridSize;
    this.cameras.main.setBounds(0, 0, worldWidth, worldHeight);
    if (this.physics && this.physics.world) {
      this.physics.world.setBounds(0, 0, worldWidth, worldHeight);
    }

    // 🚦 Signal to outer app that map is fully ready (frontend ACK trigger)
    try {
      // Keep a simple local flag for other systems if needed
      this.registry?.set('mapReady', true);
      // Dispatch a DOM event that CompleteTest page listens for
      window.dispatchEvent(new CustomEvent('ABR_MAP_READY', { detail: { width: this.mapWidth, height: this.mapHeight, tile: this.gridSize } }));
      // Legacy ui:ready 已废弃，握手改由 ui:scene:ready 统一触发
    // 后端权威地图模式：不再向后端发送掩码（ResourceManager 已直接依赖 MapManager）
    } catch (e) {
      console.warn('Map ready signaling failed:', (e as Error).message);
    }

    // ☁️ 地图生成完成后，启动云层动画系统
    this.initializeCloudLayer();

    // 🐉 创建飞龙帧动画并启动飞龙动画系统
    this.createDragonAnimation();
    this.initializeDragonLayer();
  }

  // （移除后端地图强制覆盖，保留前端生成算法）

  // --- Auto-tiler helpers (8-neighborhood) ---
  private generateAutoTileMap(family: 'WATER'|'SAND', mask: number[][]): string[][] {
    const H = mask.length, W = mask[0].length;
    const tiles: string[][] = Array.from({length:H}, ()=>Array(W).fill(family+ '_FULL'));
    for (let y=0;y<H;y++){
      for (let x=0;x<W;x++){
        if (mask[y][x]!==1){ tiles[y][x]='PLAIN'; continue; }
        const n = this.isFilled(mask,x,y-1);
        const e = this.isFilled(mask,x+1,y);
        const s = this.isFilled(mask,x,y+1);
        const w = this.isFilled(mask,x-1,y);
        const ne = this.isFilled(mask,x+1,y-1);
        const se = this.isFilled(mask,x+1,y+1);
        const sw = this.isFilled(mask,x-1,y+1);
        const nw = this.isFilled(mask,x-1,y-1);
        const pref = family + '_';
        if (n && e && s && w){
          // 🔧 优化: 统计缺失的对角线数量，正确处理窄河流
          const missingCorners = [!nw, !ne, !sw, !se].filter(Boolean).length;

          if (missingCorners === 1) {
            // 单个对角线缺失 → INNER corner (原逻辑)
            if (!nw) tiles[y][x]=pref+'INNER_TOP_LEFT';
            else if (!ne) tiles[y][x]=pref+'INNER_TOP_RIGHT';
            else if (!sw) tiles[y][x]=pref+'INNER_BOTTOM_LEFT';
            else tiles[y][x]=pref+'INNER_BOTTOM_RIGHT';
          }
          else if (missingCorners === 2) {
            // 对边缺失 → EDGE (直线窄河流)
            if (!nw && !ne) tiles[y][x]=pref+'EDGE_BOTTOM';      // 上方两角缺失
            else if (!sw && !se) tiles[y][x]=pref+'EDGE_TOP';    // 下方两角缺失
            else if (!nw && !sw) tiles[y][x]=pref+'EDGE_RIGHT';  // 左侧两角缺失
            else if (!ne && !se) tiles[y][x]=pref+'EDGE_LEFT';   // 右侧两角缺失

            // 🔧 对角缺失 → INNER (对角线窄河流显示陆地角) - 拓扑感知版本
            else if (!nw && !se) {
              // NW+SE缺失, 检查北方和西方的陆地连续性
              const northLand = !n || !this.isFilled(mask,x,y-2);
              const westLand = !w || !this.isFilled(mask,x-2,y);
              const southLand = !s || !this.isFilled(mask,x,y+2);
              const eastLand = !e || !this.isFilled(mask,x+2,y);

              // 如果北方或西方有更多陆地 → 使用INNER_TOP_RIGHT (显示左上角陆地)
              // 如果南方或东方有更多陆地 → 使用INNER_BOTTOM_LEFT (显示右下角陆地)
              if (northLand || westLand) tiles[y][x] = pref+'INNER_TOP_LEFT';
              else tiles[y][x] = pref+'INNER_BOTTOM_RIGHT';
            }
            else if (!ne && !sw) {
              // NE+SW缺失, 检查方向连续性
              const northLand = !n || !this.isFilled(mask,x,y-2);
              const eastLand = !e || !this.isFilled(mask,x+2,y);
              const southLand = !s || !this.isFilled(mask,x,y+2);
              const westLand = !w || !this.isFilled(mask,x-2,y);

              // 如果北方或东方有更多陆地 → 使用INNER_TOP_RIGHT (显示右上角陆地)
              // 如果南方或西方有更多陆地 → 使用INNER_BOTTOM_LEFT (显示左下角陆地)
              if (northLand || eastLand) tiles[y][x] = pref+'INNER_TOP_RIGHT';
              else tiles[y][x] = pref+'INNER_BOTTOM_LEFT';
            }
            else {
              tiles[y][x]=pref+'FULL'; // fallback
            }
          }
          else if (missingCorners === 3) {
            // 三对角线缺失 → 极窄通道，选择主导边
            if (nw) tiles[y][x]=pref+'EDGE_BOTTOM';
            else if (ne) tiles[y][x]=pref+'EDGE_BOTTOM';
            else if (sw) tiles[y][x]=pref+'EDGE_TOP';
            else tiles[y][x]=pref+'EDGE_TOP';
          }
          else {
            // 0个或4个缺失 → FULL
            tiles[y][x]=pref+'FULL';
          }
          continue;
        }
        const connected = [n,e,s,w].filter(Boolean).length;
        if (connected===3){
          if (!n) tiles[y][x]=pref+'EDGE_TOP';
          else if (!e) tiles[y][x]=pref+'EDGE_RIGHT';
          else if (!s) tiles[y][x]=pref+'EDGE_BOTTOM';
          else tiles[y][x]=pref+'EDGE_LEFT';
          continue;
        }
        if (connected===2){
          if (e&&s && !n && !w) tiles[y][x]=pref+'CORNER_TOP_LEFT';
          else if (w&&s && !n && !e) tiles[y][x]=pref+'CORNER_TOP_RIGHT';
          else if (e&&n && !s && !w) tiles[y][x]=pref+'CORNER_BOTTOM_LEFT';
          else if (w&&n && !s && !e) tiles[y][x]=pref+'CORNER_BOTTOM_RIGHT';
          else if (n&&s) tiles[y][x]=pref+'EDGE_LEFT';
          else if (e&&w) tiles[y][x]=pref+'EDGE_TOP';
          else tiles[y][x]=pref+'FULL';
          continue;
        }
        if (connected===1){
          if (n) tiles[y][x]=pref+'EDGE_BOTTOM';
          else if (e) tiles[y][x]=pref+'EDGE_LEFT';
          else if (s) tiles[y][x]=pref+'EDGE_TOP';
          else tiles[y][x]=pref+'EDGE_RIGHT';
          continue;
        }
        tiles[y][x]=pref+'FULL';
      }
    }
    return tiles;
  }

  private isFilled(mask:number[][], x:number,y:number): boolean {
    if (y<0||y>=mask.length||x<0||x>=mask[0].length) return false;
    return mask[y][x]===1;
  }

  // 🌊 海洋外圈强制贴图逻辑（匹配water-terrain-generator.html）
  private applyOceanRingTiles(tileMap: string[][], waterMask: number[][]): void {
    const h = waterMask.length, w = waterMask[0].length;

    // 四个角强制使用INNER瓦片（纯水域，无陆地元素）
    tileMap[0][0] = 'WATER_INNER_BOTTOM_RIGHT';        // 左上角
    tileMap[0][w-1] = 'WATER_INNER_BOTTOM_LEFT';       // 右上角
    tileMap[h-1][0] = 'WATER_INNER_TOP_RIGHT';         // 左下角
    tileMap[h-1][w-1] = 'WATER_INNER_TOP_LEFT';        // 右下角

    // 四条边强制使用EDGE瓦片（排除角）
    for (let x = 1; x < w-1; x++) {
      if (waterMask[0][x] === 1) tileMap[0][x] = 'WATER_EDGE_BOTTOM';      // 上边
      if (waterMask[h-1][x] === 1) tileMap[h-1][x] = 'WATER_EDGE_TOP';     // 下边
    }
    for (let y = 1; y < h-1; y++) {
      if (waterMask[y][0] === 1) tileMap[y][0] = 'WATER_EDGE_RIGHT';       // 左边
      if (waterMask[y][w-1] === 1) tileMap[y][w-1] = 'WATER_EDGE_LEFT';    // 右边
    }

    console.log(`🌊 海洋外圈强制贴图完成: 四角使用INNER瓦片，四边使用EDGE瓦片`);
  }

  // 🗺️ Phase B.2: 获取Base层季节性素材键值 (支持WATER/RIVER水域系统)
  // 覆盖物素材键（直接映射 /assets/tilemap 文件名）
  private getOverlayAssetKey(overlayType: string): string {
    switch (overlayType) {
      case 'FOREST':
      case 'FOREST01':
        return 'FOREST01';
      case 'FOREST02':
        return 'FOREST02';
      case 'MOUNTAIN':
        return 'MOUNTAIN';
      case 'BUILDING':
      case 'BUILDING01':
        return 'BUILDING01';
      case 'BUILDING02':
        return 'BUILDING02';
      case 'FENCE':
        return 'FENCE';
      default:
        return 'FOREST01';
    }
  }

  // 🌸 Phase B.1: 获取季节性素材键值 (保留兼容性)
  private getSeasonalAssetKey(terrainType: string): string {
    // 根据地形类型返回对应的季节素材键值
    switch (terrainType) {
      case 'plain':
        return 'plain'; // SeasonalAssetManager中定义的键值
      case 'forest':
        return 'forest01'; // 使用forest01作为默认森林素材
      case 'mountain':
        return 'mountain'; 
      case 'building':
        return 'fence'; // 使用fence作为默认建筑素材
      case 'water':
        return 'water'; // 海洋水域
      case 'river':
        return 'river'; // 内陆河流
      default:
        console.warn(`🚧 未知地形类型: ${terrainType}, 使用plain作为默认素材`);
        return 'plain';
    }
  }

  // 🧭 网格线叠加层（用于对齐校验）
  private drawGridOverlay(): void {
    if (!this.showGridOverlay) {
      if (this.gridGraphics) { this.gridGraphics.destroy(); this.gridGraphics = null; }
      return;
    }

    const widthPx = this.mapWidth * this.gridSize;
    const heightPx = this.mapHeight * this.gridSize;

    if (!this.gridGraphics) {
      this.gridGraphics = this.add.graphics();
      this.gridGraphics.setDepth(10); // 在地形之上、低于Agent
    }

    const g = this.gridGraphics;
    g.clear();
    // 深黑色网格线，提升可见度
    g.lineStyle(1, 0x000000, 0.55);

    // 竖线
    for (let x = 0; x <= this.mapWidth; x++) {
      const px = x * this.gridSize;
      g.beginPath();
      g.moveTo(px, 0);
      g.lineTo(px, heightPx);
      g.strokePath();
    }

    // 横线
    for (let y = 0; y <= this.mapHeight; y++) {
      const py = y * this.gridSize;
      g.beginPath();
      g.moveTo(0, py);
      g.lineTo(widthPx, py);
      g.strokePath();
    }
  }

  // Heatmap overlay removed (deprecated)

  // 🗺️ Phase B.2: 旧的generateTerrainType方法已移除，现在使用DynamicMapGenerator

  // 🗺️ Phase B.2: 旧的getTerrainDisplay方法已移除，现在使用双层地图系统

  // 🗺️ Phase B.2: 旧的updateTerrainTile方法已移除，现在使用双层地图系统

  // 🆕 获取格子坐标对应的像素位置
  getPixelPosition(gridX: number, gridY: number): { x: number; y: number } {
    return {
      x: gridX * this.gridSize + this.gridSize / 2,
      y: gridY * this.gridSize + this.gridSize / 2
    };
  }

  // 🆕 获取像素位置对应的格子坐标
  getGridPosition(pixelX: number, pixelY: number): { x: number; y: number } {
    return {
      x: Math.floor(pixelX / this.gridSize),
      y: Math.floor(pixelY / this.gridSize)
    };
  }

  // 🔥 SafeZone Animation Enhancement Methods

  /**
   * @deprecated 禁止直接使用SafeZone动画API，请通过Engine.handle()路由
   */
  animateSafeZoneShrink(centerGridX: number, centerGridY: number, fromRadius: number, toRadius: number, duration: number): void {
    console.warn('🚨 Deprecated API: animateSafeZoneShrink - 请通过Engine.handle()路由SafeZone动画');
    const centerPixel = this.getPixelPosition(centerGridX, centerGridY);
    
    this.safeZoneState = 'shrinking';
    
    const animationTween = this.tweens.add({
      targets: { radius: fromRadius },
      radius: toRadius,
      duration: duration,
      ease: 'Power2',
      onUpdate: (_tween: Phaser.Tweens.Tween, target: any) => {
        const currentRadius = target.radius;
        this.updateSafeZoneVisual(centerPixel.x, centerPixel.y, currentRadius * this.gridSize);
        
        this.safeZoneCallbacks.onUpdate.forEach(callback => {
          callback(currentRadius, fromRadius, toRadius);
        });
      },
      onComplete: () => {
        this.safeZoneCallbacks.onComplete.forEach(callback => {
          callback(toRadius);
        });
      }
    });
    
    this.safeZoneAnimationTweens.push(animationTween);
  }

  /**
   * @deprecated 禁止直接使用SafeZone动画API，请通过Engine.handle()路由
   */
  showSafeZoneWarning(centerGridX: number, centerGridY: number, _fromRadius: number, toRadius: number): void {
    console.warn('🚨 Deprecated API: showSafeZoneWarning - 请通过Engine.handle()路由SafeZone动画');
    const centerPixel = this.getPixelPosition(centerGridX, centerGridY);
    
    if (!this.safeZoneWarningGraphics) {
      this.safeZoneWarningGraphics = this.add.graphics();
      // 🔑 关键修复：设置Warning Graphics为最高深度
      this.safeZoneWarningGraphics.setDepth(1001); // 比SafeZone更高
    }
    
    this.safeZoneWarningGraphics.clear();
    this.safeZoneWarningGraphics.lineStyle(2, 0xff9900, 0.8);
    this.safeZoneWarningGraphics.strokeCircle(centerPixel.x, centerPixel.y, toRadius * this.gridSize);
    this.safeZoneWarningGraphics.fillStyle(0xff9900, 0.1);
    this.safeZoneWarningGraphics.fillCircle(centerPixel.x, centerPixel.y, toRadius * this.gridSize);
    
    // 🔧 修改：移除橙色脉冲动画警告，改为静态显示
    this.safeZoneWarningGraphics.alpha = 0.8;
  }

  interpolateSafeZoneRadius(fromRadius: number, toRadius: number, progress: number): number {
    return fromRadius + (toRadius - fromRadius) * progress;
  }

  setSafeZoneState(state: string, centerGridX: number, centerGridY: number, radius: number): void {
    this.safeZoneState = state;
    const centerPixel = this.getPixelPosition(centerGridX, centerGridY);
    const radiusPixels = radius * this.gridSize;
    
    if (!this.safeZone) {
      this.safeZone = this.add.graphics();
    }
    
    this.safeZone.clear();
    
    const stateConfig = this.getSafeZoneStateConfig(state);
    
    this.safeZone.lineStyle(3, stateConfig.color, 0.9);
    this.safeZone.strokeCircle(centerPixel.x, centerPixel.y, radiusPixels);
    this.safeZone.fillStyle(stateConfig.color, stateConfig.alpha);
    this.safeZone.fillCircle(centerPixel.x, centerPixel.y, radiusPixels);
    
    if (state === 'warning') {
      const pulsingTween = this.tweens.add({
        targets: this.safeZone,
        alpha: { from: 1, to: 0.5 },
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
      this.safeZoneAnimationTweens.push(pulsingTween);
    }
  }

  destroySafeZoneAnimations(): void {
    this.safeZoneAnimationTweens.forEach(tween => {
      this.tweens.killTweensOf(tween.targets);
    });
    this.safeZoneAnimationTweens = [];
    
    if (this.safeZoneWarningGraphics) {
      this.safeZoneWarningGraphics.destroy();
      this.safeZoneWarningGraphics = null;
    }
    
    if (this.safeZoneTimer) {
      this.safeZoneTimer.destroy();
      this.safeZoneTimer = null;
    }
  }

  startSafeZoneShrinking(centerGridX: number, centerGridY: number, fromRadius: number, toRadius: number, duration: number): void {
    this.showSafeZoneWarning(centerGridX, centerGridY, fromRadius, toRadius);
    
    this.time.delayedCall(2000, () => {
      this.animateSafeZoneShrink(centerGridX, centerGridY, fromRadius, toRadius, duration);
    });
  }

  highlightAgentsOutsideSafeZone(centerGridX: number, centerGridY: number, currentRadius: number): void {
    if (!this.safeZoneWarningGraphics) {
      this.safeZoneWarningGraphics = this.add.graphics();
      // 🔑 关键修复：设置Warning Graphics为最高深度
      this.safeZoneWarningGraphics.setDepth(1001); // 比SafeZone更高
    }
    
    const centerPixel = this.getPixelPosition(centerGridX, centerGridY);
    
    this.agents.forEach((agentContainer, _agentId) => {
      const distance = Phaser.Math.Distance.Between(agentContainer.x, agentContainer.y, centerPixel.x, centerPixel.y);
      
      if (distance > currentRadius * this.gridSize) {
        this.safeZoneWarningGraphics!.lineStyle(2, 0xff0000, 0.8);
        this.safeZoneWarningGraphics!.strokeCircle(agentContainer.x, agentContainer.y, 25);
      }
    });
  }

  onSafeZoneAnimationComplete(callback: Function): void {
    this.safeZoneCallbacks.onComplete.push(callback);
  }

  onSafeZoneAnimationUpdate(callback: Function): void {
    this.safeZoneCallbacks.onUpdate.push(callback);
  }

  showSafeZoneTimer(remainingTime: number): void {
    if (!this.safeZoneTimer) {
      this.safeZoneTimer = this.add.text(this.cameras.main.width - 150, 50, '', {
        fontSize: '16px',
        color: '#ff6600',
        backgroundColor: '#000000',
        padding: { x: 8, y: 4 }
      }).setScrollFactor(0);
    }
    
    this.safeZoneTimer.setText(`SafeZone: ${remainingTime}s`);
  }

  private getSafeZoneStateConfig(state: string): { color: number, alpha: number } {
    switch (state) {
      case 'normal': return { color: 0x00ff00, alpha: 0.3 };
      case 'warning': return { color: 0xff9900, alpha: 0.5 };
      case 'shrinking': return { color: 0xff6600, alpha: 0.7 };
      case 'critical': return { color: 0xff0000, alpha: 0.9 };
      default: return { color: 0x00ff00, alpha: 0.3 };
    }
  }

  private updateSafeZoneVisual(centerX: number, centerY: number, radiusPixels: number): void {
    if (this.safeZone) {
      this.safeZone.clear();
      const stateConfig = this.getSafeZoneStateConfig(this.safeZoneState);
      this.safeZone.lineStyle(3, stateConfig.color, 0.9);
      this.safeZone.strokeCircle(centerX, centerY, radiusPixels);
      // 🔧 修复：移除安全区内部填充，只保留边框线条
      // this.safeZone.fillStyle(stateConfig.color, stateConfig.alpha);
      // this.safeZone.fillCircle(centerX, centerY, radiusPixels);
    }
  }






  // Public methods for external interaction
  public isWebSocketConnected(): boolean {
    return webSocketService.isConnected();
  }

  public sendPlayerAction(action: string, payload: any): void {
    try {
      if (webSocketService.isConnected()) {
        // 使用WebSocketService发送玩家行动
        webSocketService.sendMessage('PLAYER_ACTION', { action, payload });
        console.log('📤 Sent player action via WebSocketService:', { action, payload });
      } else {
        console.warn('⚠️ Cannot send player action - WebSocket not connected');
      }
    } catch (error) {
      console.error('Failed to send player action:', error);
    }
  }

  // 🔥 Phase 1.2: State synchronization methods
  public handleGameStateUpdate(update: GameStateUpdate): void {
    if (!this.validateStateUpdate(update)) {
      console.warn('Invalid game state update:', update);
      return;
    }
    // Process full game state update
    console.log('Processing game state update with sequence:', update.sequence);
  }

  public handleAgentStateUpdate(update: AgentStateUpdate): void {
    if (!this.validateStateUpdate(update)) {
      console.warn('Invalid agent state update:', update);
      return;
    }
    // Process agent state update
    console.log('Processing agent state update for:', update.data.agentId);
  }

  public handleResourceStateUpdate(update: ResourceStateUpdate): void {
    if (!this.validateStateUpdate(update)) {
      console.warn('Invalid resource state update:', update);
      return;
    }
    // Process resource state update
    console.log('Processing resource state update for:', update.data.resourceId);
  }

  public handleSafeZoneStateUpdate(update: SafeZoneStateUpdate): void {
    if (!this.validateStateUpdate(update)) {
      console.warn('Invalid safezone state update:', update);
      return;
    }
    // Process safezone state update
    console.log('Processing safezone state update, radius:', update.data.currentRadius);
  }

  public handleCombatStateUpdate(update: CombatStateUpdate): void {
    if (!this.validateStateUpdate(update)) {
      console.warn('Invalid combat state update:', update);
      return;
    }
    // Process combat state update
    console.log('Processing combat state update:', update.data.combatId);
  }

  public validateStateUpdate(update: any): boolean {
    if (!update || typeof update !== 'object') {
      return false;
    }
    
    // Check required fields
    const requiredFields = ['type', 'timestamp', 'sequence'];
    if (!requiredFields.every(field => field in update)) {
      return false;
    }

    // Validate timestamp format
    if (isNaN(Date.parse(update.timestamp))) {
      return false;
    }

    // Validate sequence number
    if (!Number.isInteger(update.sequence) || update.sequence < 0) {
      return false;
    }

    return true;
  }

  public handleSequencedUpdate(update: AnyStateUpdate): void {
    const { sequence } = update;
    
    // Check for out-of-order updates
    if (sequence <= this.lastSequenceNumber) {
      console.warn(`Out of order update received: ${sequence}, expected > ${this.lastSequenceNumber}`);
      return;
    }

    // Check for sequence gaps
    if (sequence > this.lastSequenceNumber + 1) {
      // Store update for later processing
      this.sequenceBuffer.set(sequence, update);
      console.log(`Buffering update ${sequence}, waiting for ${this.lastSequenceNumber + 1}`);
      return;
    }

    // Process current update
    this.processStateUpdate(update);
    this.lastSequenceNumber = sequence;

    // Process any buffered updates that are now in sequence
    this.processBufferedUpdates();
  }

  private processStateUpdate(update: AnyStateUpdate): void {
    console.log('Processing state update:', update.type, 'sequence:', update.sequence);
    // Individual update processing is handled by specific handlers
  }

  private processBufferedUpdates(): void {
    let nextSequence = this.lastSequenceNumber + 1;
    
    while (this.sequenceBuffer.has(nextSequence)) {
      const bufferedUpdate = this.sequenceBuffer.get(nextSequence)!;
      this.processStateUpdate(bufferedUpdate);
      this.sequenceBuffer.delete(nextSequence);
      this.lastSequenceNumber = nextSequence;
      nextSequence++;
    }

    // Clean up old buffered updates if buffer gets too large
    if (this.sequenceBuffer.size > STATE_SYNC_CONFIG.SEQUENCE_BUFFER_SIZE) {
      const oldestAllowed = this.lastSequenceNumber - STATE_SYNC_CONFIG.MAX_SEQUENCE_GAP;
      for (const [seq] of this.sequenceBuffer) {
        if (seq < oldestAllowed) {
          this.sequenceBuffer.delete(seq);
        }
      }
    }
  }

  // ==================== Task 1.2.2: Direct GameEngine State Reception ====================
  
  // Entity management maps
  private readonly agentSprites = new Map<string, Phaser.GameObjects.Sprite>();
  private readonly agentHealthBars = new Map<string, Phaser.GameObjects.Graphics>();
  private readonly agentNameTexts = new Map<string, Phaser.GameObjects.Text>();
  private readonly resourceSprites = new Map<string, Phaser.GameObjects.Sprite>();
  
  // SafeZone state
  private safeZoneGraphics?: { centerX: number; centerY: number; radius: number; graphics: Phaser.GameObjects.Graphics };
  
  // Performance optimization
  private readonly spritePool: Phaser.GameObjects.Sprite[] = [];
  private processedUpdateCount = 0;

  public processGameStateUpdate(update: GameStateUpdate): void {
    const { data } = update;
    
    // Update game context
    if (data.context) {
      this.updateGameContext(data.context);
    }
    
    // Update all agents
    if (data.agents) {
      this.syncAgentSprites(data.agents);
    }
    
    // Update resources
    if (data.resources) {
      this.syncResourceSprites(data.resources);
    }
    
    // Update safe zone
    if (data.safeZone) {
      this.updateSafeZoneGraphics(data.safeZone);
    }
    
    this.processedUpdateCount++;
  }

  public processAgentStateUpdate(update: AgentStateUpdate): void {
    const { data } = update;
    const sprite = this.agentSprites.get(data.agentId);

    if (!sprite) {
      // 🔧 替换：通过Engine架构创建Agent而不是直接创建sprite
      console.log(`🎯 processAgentStateUpdate: 通过Engine创建Agent ${data.agentId}`);
      this.animationEngine?.handle({
        type: 'AGENT_SPAWNED',
        agentId: data.agentId,
        data: {
          agent: {
            id: data.agentId,
            position: data.position || { x: 0, y: 0 },
            color: data.color || 'green',
            health: data.health || 100
          }
        }
      });
    } else {
      // Update existing sprite
      if (data.position) {
        sprite.setPosition(data.position.x, data.position.y);
      }
    }
    
    // Update health bar if health changed
    if (data.health) {
      this.updateAgentHealthBar(data.agentId, data.health);
    }
    
    // Update LLM thinking indicator
    if (data.llmThinking !== undefined) {
      this.updateLLMThinkingIndicator(data.agentId, data.llmThinking);
    }
    
    this.processedUpdateCount++;
  }

  public processResourceStateUpdate(update: ResourceStateUpdate): void {
    const { data } = update;
    
    if (data.status === 'spawned' && data.position) {
      // Prefer ResourceSystem for creation
      if (this.resourceSystem) {
        const key = String(data.resourceType || '').toLowerCase();
        const category = (key.includes('sword') || key.includes('axe') || key.includes('lance')) ? 'weapon' : 'medical';
        const weaponType = category === 'weapon' ? (key.includes('sword') ? 'SWORD' : (key.includes('axe') ? 'AXE' : 'LANCE')) : undefined;
        const medicalType = category === 'medical' ? (key.includes('small') ? 'small' : 'large') : undefined;
        this.resourceSystem.spawnResource({
          itemId: data.resourceId,
          type: category === 'weapon' ? 'WEAPON' : 'CONSUMABLE',
          category: category as any,
          weaponType: weaponType as any,
          medicalType: medicalType as any,
          position: data.position
        });
      } else {
        // Fallback legacy
        this.createResourceSprite(data.resourceId, data.resourceType, data.position);
      }
    } else if (data.status === 'disappeared') {
      // 立即移除（非采集触发）
      this.removeResourceSprite(data.resourceId);
    } else if (data.status === 'collected') {
      // 采集导致的移除由 Engine 统一时序控制（先移动，后移除）
      // 这里不直接移除，避免越过动画队列
      // 可选：若缺少事件桥，可构造一个 RESOURCE_COLLECTED 事件再交给 Engine
    }
    
    this.processedUpdateCount++;
  }

  public processSafeZoneStateUpdate(update: SafeZoneStateUpdate): void {
    const { data } = update;
    this.updateSafeZoneGraphics({
      centerX: data.centerX,
      centerY: data.centerY,
      currentRadius: data.currentRadius,
      targetRadius: data.targetRadius,
      contractionTime: data.contractionTime,
      isContracting: data.isContracting,
      warningTime: data.warningTime
    });
    this.processedUpdateCount++;
  }

  public processCombatStateUpdate(update: CombatStateUpdate): void {
    const { data } = update;
    
    // Play combat animation
    this.playCombatAnimation({
      attackerId: data.attackerId,
      targetId: data.targetId,
      weapon: data.weapon,
      damage: data.damage,
      result: data.result
    });
    
    // Update health bars
    this.updateAgentHealthBar(data.attackerId, data.attackerHealth);
    this.updateAgentHealthBar(data.targetId, data.targetHealth);
    
    this.processedUpdateCount++;
  }

  public processBatchUpdates(updates: AnyStateUpdate[]): void {
    updates.forEach(update => {
      switch (update.type) {
        case 'GAME_STATE_UPDATE':
          this.processGameStateUpdate(update as GameStateUpdate);
          break;
        case 'AGENT_STATE_UPDATE':
          this.processAgentStateUpdate(update as AgentStateUpdate);
          break;
        case 'RESOURCE_STATE_UPDATE':
          this.processResourceStateUpdate(update as ResourceStateUpdate);
          break;
        case 'SAFEZONE_STATE_UPDATE':
          this.processSafeZoneStateUpdate(update as SafeZoneStateUpdate);
          break;
        case 'COMBAT_STATE_UPDATE':
          this.processCombatStateUpdate(update as CombatStateUpdate);
          break;
      }
    });
  }

  // 🗑️ createAgentSprite方法已删除 - 所有Agent创建现在通过Engine架构处理
  // 旧方法问题：硬编码绿色纹理、重复功能、架构冲突
  // 新架构：WebSocket → BattleScene → Engine → SceneAdapter → PhaserAnimationManager

  // ☁️ ==================== 云层动画系统 ====================

  private initializeCloudLayer(): void {
    console.log('☁️ 初始化云层动画系统');

    // 创建云层容器
    this.cloudLayer = this.add.group();

    // 启动云朵生成定时器 (每8-15秒生成一朵云)
    this.cloudSpawnTimer = this.time.addEvent({
      delay: Phaser.Math.Between(8000, 15000),
      callback: () => {
        this.spawnCloud();
        // 重置下一次生成的随机间隔
        if (this.cloudSpawnTimer) {
          this.cloudSpawnTimer.reset({
            delay: Phaser.Math.Between(8000, 15000),
            callback: () => this.spawnCloud(),
            loop: false
          });
        }
      },
      loop: false
    });
  }

  private spawnCloud(): void {
    // 限制最大云朵数量
    if (this.activeClouds.size >= this.MAX_CLOUDS) {
      return;
    }

    // 随机选择云朵类型
    const cloudType = Phaser.Math.RND.pick(['CLOUD01', 'CLOUD02']);

    // 随机方向：50%从左向右，50%从右向左
    const direction = Phaser.Math.RND.pick(['left', 'right']) as 'left' | 'right';

    // 计算地图尺寸
    const worldWidth = this.mapWidth * this.gridSize;
    const worldHeight = this.mapHeight * this.gridSize;

    // 获取云朵纹理尺寸
    const cloudTexture = this.textures.get(cloudType);
    const cloudWidth = cloudTexture.getSourceImage().width;
    const cloudHeight = cloudTexture.getSourceImage().height;

    // 随机Y坐标 (留出上下边距，避免云朵被裁切)
    const yPos = Phaser.Math.Between(cloudHeight / 2, worldHeight - cloudHeight / 2);

    // 根据方向设置起始和结束位置
    let xStart: number, xEnd: number;
    if (direction === 'left') {
      xStart = -cloudWidth;
      xEnd = worldWidth + cloudWidth;
    } else {
      xStart = worldWidth + cloudWidth;
      xEnd = -cloudWidth;
    }

    // 创建云朵精灵
    const cloud = this.add.image(xStart, yPos, cloudType);
    cloud.setDepth(this.CLOUD_DEPTH);
    cloud.setAlpha(0.8); // 半透明效果

    // 添加到容器和激活集合
    this.cloudLayer?.add(cloud);
    this.activeClouds.add(cloud);

    // 随机速度 (12.5-20像素/秒，比原速度慢50%)
    const speed = Phaser.Math.Between(12.5, 20);
    const distance = Math.abs(xEnd - xStart);
    const duration = (distance / speed) * 1000; // 转换为毫秒

    console.log(`☁️ 生成云朵: ${cloudType}, 方向: ${direction}, 速度: ${speed}px/s, 耗时: ${(duration/1000).toFixed(1)}s`);

    // 创建移动动画
    this.tweens.add({
      targets: cloud,
      x: xEnd,
      duration: duration,
      ease: 'Linear',
      onComplete: () => {
        this.cleanupCloud(cloud);
      }
    });
  }

  private cleanupCloud(cloud: Phaser.GameObjects.Image): void {
    // 从激活集合中移除
    this.activeClouds.delete(cloud);

    // 从容器中移除
    this.cloudLayer?.remove(cloud);

    // 销毁云朵对象
    cloud.destroy();
  }

  public toggleClouds(enabled: boolean): void {
    if (enabled) {
      if (!this.cloudLayer) {
        this.initializeCloudLayer();
      }
      this.cloudLayer?.setVisible(true);
      console.log('☁️ 云层显示已启用');
    } else {
      this.cloudLayer?.setVisible(false);
      // 停止生成定时器
      this.cloudSpawnTimer?.remove();
      this.cloudSpawnTimer = null;
      console.log('☁️ 云层显示已禁用');
    }
  }

  // ☁️ ==================== 云层动画系统结束 ====================

  // 🐉 ==================== 飞龙动画系统开始 ====================

  /**
   * 创建飞龙的帧动画
   */
  private createDragonAnimation(): void {
    // 检查动画是否已存在，避免重复创建
    if (this.anims.exists('dragon_fly')) {
      return;
    }

    // 创建飞龙飞行动画（循环播放3帧）
    this.anims.create({
      key: 'dragon_fly',
      frames: [
        { key: 'DRAGON_1' },
        { key: 'DRAGON_2' },
        { key: 'DRAGON_3' }
      ],
      frameRate: 2.67, // 每秒2.67帧，速率放慢3倍（原8fps/3）
      repeat: -1 // 无限循环
    });

    console.log('🐉 飞龙帧动画已创建');
  }

  /**
   * 初始化飞龙图层和生成定时器
   */
  private initializeDragonLayer(): void {
    console.log('🐉 初始化飞龙动画系统');

    // 创建飞龙层容器
    this.dragonLayer = this.add.group();

    // 启动飞龙生成定时器 (每5分钟生成一只龙，稀有事件)
    this.dragonSpawnTimer = this.time.addEvent({
      delay: 300000, // 5分钟 = 300秒 = 300000毫秒
      callback: () => {
        this.spawnDragon();
        // 重置下一次生成的固定间隔
        if (this.dragonSpawnTimer) {
          this.dragonSpawnTimer.reset({
            delay: 300000, // 固定5分钟间隔
            callback: () => this.spawnDragon(),
            loop: false
          });
        }
      },
      loop: false
    });
  }

  /**
   * 生成一只飞龙
   */
  private spawnDragon(): void {
    // 限制最大飞龙数量
    if (this.activeDragons.size >= this.MAX_DRAGONS) {
      return;
    }

    // 随机方向：50%从左向右，50%从右向左
    const direction = Phaser.Math.RND.pick(['left', 'right']) as 'left' | 'right';

    // 计算地图尺寸
    const worldWidth = this.mapWidth * this.gridSize;
    const worldHeight = this.mapHeight * this.gridSize;

    // 龙的基准尺寸（根据实际图片大小调整，假设每帧约140x134px）
    const dragonWidth = 140;
    const dragonHeight = 134;

    // 随机Y坐标 (留出上下边距，避免龙被裁切)
    const yPos = Phaser.Math.Between(dragonHeight / 2 + 50, worldHeight - dragonHeight / 2 - 50);

    // 根据方向设置起始和结束位置
    let xStart: number, xEnd: number;
    if (direction === 'left') {
      xStart = -dragonWidth;
      xEnd = worldWidth + dragonWidth;
    } else {
      xStart = worldWidth + dragonWidth;
      xEnd = -dragonWidth;
    }

    // 创建飞龙精灵（使用第一帧作为初始纹理）
    const dragon = this.add.sprite(xStart, yPos, 'DRAGON_1');
    dragon.setDepth(this.DRAGON_DEPTH);
    dragon.setScale(1); // 保持原始尺寸，可根据需要调整
    dragon.setAlpha(0.9); // 略微透明

    // 原图龙头向左，从左向右飞时需要镜像翻转
    if (direction === 'left') {
      dragon.setFlipX(true);
    }

    // 播放飞龙飞行动画
    dragon.play('dragon_fly');

    // 添加到容器和激活集合
    this.dragonLayer?.add(dragon);
    this.activeDragons.add(dragon);

    // 随机速度 (参考云朵速度12.5-20px/s，龙稍快一些: 20-30px/s)
    const speed = Phaser.Math.Between(20, 30);
    const distance = Math.abs(xEnd - xStart);
    const duration = (distance / speed) * 1000; // 转换为毫秒

    console.log(`🐉 生成飞龙: 方向=${direction === 'left' ? '左→右' : '右→左'}, 速度=${speed}px/s, 耗时=${(duration/1000).toFixed(1)}s, 下次生成时间: 5分钟后`);

    // 创建移动动画
    this.tweens.add({
      targets: dragon,
      x: xEnd,
      duration: duration,
      ease: 'Linear',
      onComplete: () => {
        this.cleanupDragon(dragon);
      }
    });
  }

  /**
   * 清理飞龙对象
   */
  private cleanupDragon(dragon: Phaser.GameObjects.Sprite): void {
    // 从激活集合中移除
    this.activeDragons.delete(dragon);

    // 从容器中移除
    this.dragonLayer?.remove(dragon);

    // 销毁龙对象
    dragon.destroy();
  }

  /**
   * 切换飞龙显示/隐藏
   */
  public toggleDragons(enabled: boolean): void {
    if (enabled) {
      if (!this.dragonLayer) {
        this.createDragonAnimation();
        this.initializeDragonLayer();
      }
      this.dragonLayer?.setVisible(true);
      console.log('🐉 飞龙显示已启用');
    } else {
      this.dragonLayer?.setVisible(false);
      // 停止生成定时器
      this.dragonSpawnTimer?.remove();
      this.dragonSpawnTimer = null;
      console.log('🐉 飞龙显示已禁用');
    }
  }

  // 🐉 ==================== 飞龙动画系统结束 ====================

  public removeAgentSprite(agentId: string): void {
    const sprite = this.agentSprites.get(agentId);
    if (sprite) {
      sprite.setActive(false);
      sprite.setVisible(false);
      this.spritePool.push(sprite);
      this.agentSprites.delete(agentId);
    }
    
    const healthBar = this.agentHealthBars.get(agentId);
    if (healthBar) {
      healthBar.destroy();
      this.agentHealthBars.delete(agentId);
    }
    
    const nameText = this.agentNameTexts.get(agentId);
    if (nameText) {
      nameText.destroy();
      this.agentNameTexts.delete(agentId);
    }
  }

  public getAgentSprite(agentId: string): Phaser.GameObjects.Sprite | undefined {
    return this.agentSprites.get(agentId);
  }

  public getAgentHealthBar(agentId: string): Phaser.GameObjects.Graphics | undefined {
    return this.agentHealthBars.get(agentId);
  }

  // 🆕 为Agent创建并集成到容器的血条Graphics（只创建不绘制）
  public createOrGetAgentHealthBar(agentId: string): Phaser.GameObjects.Graphics | undefined {
    let healthBar = this.agentHealthBars.get(agentId);
    if (healthBar && !healthBar.destroyed) return healthBar;

    if (!this.phaserAnimationManager) return undefined;
    const container = this.phaserAnimationManager.agentContainers.get(agentId);
    if (!container) return undefined;

    // 创建Graphics并加入Agent容器，使其采用容器本地坐标系
    healthBar = this.add.graphics();
    // 容器内无需设置深度；如需覆盖容器子元素，可适当调整添加顺序
    container.add(healthBar);
    this.agentHealthBars.set(agentId, healthBar);
    return healthBar;
  }

  public createResourceSprite(resourceId: string, resourceType: string, position: Position): void {
    // 统一以 Scene.gridSize 作为唯一基准（与地图 tile 完全一致）
    const pixelX = position.x * this.gridSize + this.gridSize / 2;
    const pixelY = position.y * this.gridSize + this.gridSize / 2;

    const sprite = this.add.sprite(pixelX, pixelY, resourceType);

    // 设置sprite属性
    sprite.setDepth(5);
    sprite.setOrigin(0.5, 0.5);
    // 资源缩放：按类别（由resourceType推断）设置目标边长比例
    try {
      // 推断类别
      const key = String(resourceType);
      const isMedical = key.includes('medkit');
      const isWeapon = key.includes('sword') || key.includes('axe') || key.includes('lance');
      const ratio = isMedical ? 0.7 : (isWeapon ? 0.8 : 0.5);
      const TARGET_EDGE = this.gridSize * ratio;
      const baseW = sprite.texture.getSourceImage().width;
      const baseH = sprite.texture.getSourceImage().height;
      const baseMax = Math.max(baseW, baseH);
      const ratioExact = TARGET_EDGE / baseMax;
      if (ratioExact >= 1) {
        const k = Math.max(1, Math.floor(ratioExact));
        sprite.setScale(k);
      } else {
        sprite.setScale(ratioExact);
      }
    } catch {
      const key = String(resourceType);
      const isMedical = key.includes('medkit');
      const isWeapon = key.includes('sword') || key.includes('axe') || key.includes('lance');
      sprite.setScale(isMedical ? 0.7 : (isWeapon ? 0.8 : 0.5));
    }

    // 添加到资源容器
    if (this.resourceContainer) {
      this.resourceContainer.add(sprite);
    }

    this.resourceSprites.set(resourceId, sprite);
    
  }

  public removeResourceSprite(resourceId: string): void {
    const sprite = this.resourceSprites.get(resourceId);
    if (sprite) {
      sprite.destroy();
      this.resourceSprites.delete(resourceId);
      
    }
  }

  public getResourceSprite(resourceId: string): Phaser.GameObjects.Sprite | undefined {
    return this.resourceSprites.get(resourceId);
  }

  public getSafeZoneGraphics(): { centerX: number; centerY: number; radius: number } | undefined {
    return this.safeZoneGraphics ? {
      centerX: this.safeZoneGraphics.centerX,
      centerY: this.safeZoneGraphics.centerY,
      radius: this.safeZoneGraphics.radius
    } : undefined;
  }

  /**
   * @deprecated 禁止直接播放战斗动画，请通过Engine.handle()路由战斗事件
   */
  public playCombatAnimation(params: {
    attackerId: string;
    targetId: string;
    weapon?: string;
    damage: number;
    result: string;
  }): void {
    console.warn('🚨 Deprecated API: playCombatAnimation - 请通过Engine.handle()路由战斗事件');
    // Animation implementation placeholder
    console.log('Playing combat animation:', params);
  }

  public getSpritePoolSize(): number {
    return this.spritePool.length;
  }

  public getProcessedUpdateCount(): number {
    return this.processedUpdateCount;
  }

  private updateGameContext(context: GameContext): void {
    // Update game context display
    console.log('Game context updated:', context);
  }

  private syncAgentSprites(agents: AgentState[]): void {
    const currentAgentIds = new Set(agents.map(a => a.agentId));
    
    // Remove sprites for agents no longer in state
    for (const [agentId] of this.agentSprites) {
      if (!currentAgentIds.has(agentId)) {
        this.removeAgentSprite(agentId);
      }
    }
    
    // Update or create sprites for current agents
    agents.forEach(agent => {
      const sprite = this.agentSprites.get(agent.agentId);
      if (sprite) {
        sprite.setPosition(agent.position.x, agent.position.y);
      } else {
        // 🔧 替换：通过Engine架构创建Agent
        console.log(`🎯 syncAgentSprites: 通过Engine创建Agent ${agent.agentId}`);
        this.animationEngine?.handle({
          type: 'AGENT_SPAWNED',
          agentId: agent.agentId,
          data: {
            agent: {
              id: agent.agentId,
              position: agent.position,
              color: agent.color || 'green',
              health: agent.health || 100
            }
          }
        });
      }
      
      this.updateAgentHealthBar(agent.agentId, agent.health);
    });
  }

  private syncResourceSprites(resources: ResourceState[]): void {
    const currentResourceIds = new Set(resources.map(r => r.resourceId));
    
    // Remove sprites for resources no longer in state
    for (const [resourceId] of this.resourceSprites) {
      if (!currentResourceIds.has(resourceId)) {
        this.removeResourceSprite(resourceId);
      }
    }
    
    // Update or create sprites for current resources
    resources.forEach(resource => {
      if (resource.status === 'spawned') {
        if (!this.resourceSprites.has(resource.resourceId)) {
          this.createResourceSprite(resource.resourceId, resource.resourceType, resource.position);
        }
      }
    });
  }

  private updateSafeZoneGraphics(safeZone: SafeZoneState): void {
    if (!this.safeZoneGraphics) {
      const graphics = this.add.graphics();
      this.safeZoneGraphics = {
        centerX: safeZone.centerX,
        centerY: safeZone.centerY,
        radius: safeZone.currentRadius,
        graphics
      };
    } else {
      this.safeZoneGraphics.centerX = safeZone.centerX;
      this.safeZoneGraphics.centerY = safeZone.centerY;
      this.safeZoneGraphics.radius = safeZone.currentRadius;
    }
    
    // Redraw safe zone
    const { SAFEZONE } = BattleScene.UI_CONFIG;
    const graphics = this.safeZoneGraphics.graphics;
    
    graphics.clear();
    graphics.lineStyle(SAFEZONE.LINE_WIDTH, SAFEZONE.CURRENT_COLOR, SAFEZONE.CURRENT_ALPHA);
    graphics.strokeCircle(safeZone.centerX, safeZone.centerY, safeZone.currentRadius);
    
    if (safeZone.isContracting) {
      graphics.lineStyle(SAFEZONE.LINE_WIDTH, SAFEZONE.TARGET_COLOR, SAFEZONE.TARGET_ALPHA);
      graphics.strokeCircle(safeZone.centerX, safeZone.centerY, safeZone.targetRadius);
    }
  }

  private updateAgentHealthBar(agentId: string, health: Health): void {
    // 确保血条已创建并集成到容器
    const healthBar = this.createOrGetAgentHealthBar(agentId);
    if (!healthBar) return;

    // 必须拿到容器，否则不绘制（避免在(0,0)画出黑框）
    const container = this.phaserAnimationManager?.agentContainers.get(agentId);
    if (!container || (healthBar as any).parentContainer !== container) return;

    const { HEALTH_BAR } = BattleScene.UI_CONFIG;
    const current = Math.max(0, Math.min(health.current ?? 0, health.max ?? 1));
    const max = Math.max(1, health.max ?? 1);
    const healthPercent = current / max;

    healthBar.clear();
    const lx = -HEALTH_BAR.WIDTH / 2;
    const ly = HEALTH_BAR.Y_OFFSET;
    // 背景（降低不透明度，避免在极端情况下被误认为“黑框”）
    healthBar.fillStyle(HEALTH_BAR.COLORS.BACKGROUND, 0.2);
    healthBar.fillRect(lx, ly, HEALTH_BAR.WIDTH, HEALTH_BAR.HEIGHT);
    // 前景
    const color = healthPercent > HEALTH_BAR.THRESHOLDS.WOUNDED
      ? HEALTH_BAR.COLORS.HEALTHY
      : healthPercent > HEALTH_BAR.THRESHOLDS.CRITICAL
        ? HEALTH_BAR.COLORS.WOUNDED
        : HEALTH_BAR.COLORS.CRITICAL;
    healthBar.fillStyle(color, 1);
    healthBar.fillRect(lx, ly, HEALTH_BAR.WIDTH * healthPercent, HEALTH_BAR.HEIGHT);
  
  }

  private updateLLMThinkingIndicator(agentId: string, isThinking: boolean): void {
    const sprite = this.agentSprites.get(agentId);
    if (!sprite) return;
    
    const { LLM_THINKING } = BattleScene.UI_CONFIG;
    
    if (isThinking) {
      sprite.setTint(LLM_THINKING.TINT_COLOR);
    } else {
      sprite.clearTint();
    }
  }

  // ==================== Task 1.2.3: Input Handling and Action Sending ====================

  public setupInputHandlers(): void {
    // Setup keyboard input using Phaser3 API
    this.keys = this.input.keyboard?.addKeys('W,A,S,D') as { 
      W: Phaser.Input.Keyboard.Key;
      A: Phaser.Input.Keyboard.Key;
      S: Phaser.Input.Keyboard.Key;
      D: Phaser.Input.Keyboard.Key;
    };

    // Setup mouse input
    // 🆕 v3.2.139: Enhanced camera drag with click detection
    this.cameras.main.setLerp(0.1, 0.1); // Smooth camera movement

    // Track pointer down position
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
      this.isDragging = false; // Reset dragging state
    });

    // Handle pointer movement (camera drag)
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) {
        const dx = Math.abs(pointer.x - this.dragStartX);
        const dy = Math.abs(pointer.y - this.dragStartY);
        const distance = Math.sqrt(dx * dx + dy * dy);

        // If moved beyond threshold, it's a drag
        if (distance > this.DRAG_THRESHOLD) {
          this.isDragging = true;
        }

        // Always allow camera dragging when mouse is down
        this.cameras.main.scrollX -= (pointer.x - pointer.prevPosition.x) / this.cameras.main.zoom;
        this.cameras.main.scrollY -= (pointer.y - pointer.prevPosition.y) / this.cameras.main.zoom;
      }
    });

    // Handle pointer up (distinguish click from drag)
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      // Only trigger game action if it was a click (not a drag)
      if (!this.isDragging) {
        this.handleMouseInput(pointer);
      }
      // Reset drag state
      this.isDragging = false;
    });

    // 相机初始缩放
    this.cameras.main.setZoom(1.0);

    // 🖱️ 滚轮缩放（以指针为锚点），范围 0.3–4，步进 0.9/1.1
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _go: any, _dx: number, dy: number) => {
      const cam = this.cameras.main;
      const before = cam.getWorldPoint(pointer.x, pointer.y) as any;
      const factor = dy > 0 ? 0.9 : 1.1;
      const next = Phaser.Math.Clamp(cam.zoom * factor, 0.3, 4);
      cam.setZoom(next);
      const after = cam.getWorldPoint(pointer.x, pointer.y) as any;
      cam.scrollX += (before.x - after.x);
      cam.scrollY += (before.y - after.y);
    });

    console.log('🎮 Input handlers setup complete - Camera drag enabled (drag to move, click to act)');
  }

  public handleKeyboardInput(key: string): void {
    // Early exit if scene not active
    if (!this.isActive()) return;
    // Suppress user action emission in backend-engine test mode (8081)
    try { if (String(webSocketService.getConfig().url||'').includes(':8081')) return; } catch {}

    // Apply input debouncing 
    if (!this.shouldProcessInput()) return;

    // Validate and map key to direction
    const direction = BattleScene.DIRECTION_MAP[key as keyof typeof BattleScene.DIRECTION_MAP];
    if (!direction) {
      console.warn(`Invalid key input: ${key}`);
      return;
    }

    // Create and send move action
    const moveAction: UserAction = {
      type: 'MOVE',
      timestamp: new Date().toISOString(),
      sequence: this.getNextSequenceNumber(),
      playerId: this.playerId,
      data: { direction }
    };

    this.sendUserAction(moveAction);
  }

  public handleMouseInput(pointer: Phaser.Input.Pointer): void {
    // Early exit if scene not active
    if (!this.isActive()) return;
    // Suppress user action emission in backend-engine test mode (8081)
    try { if (String(webSocketService.getConfig().url||'').includes(':8081')) return; } catch {}

    // Determine action type based on mouse button
    const actionType = pointer.button === 2 ? 'INTERACT' : 'ATTACK';
    
    // Create and send action
    const mouseAction: UserAction = {
      type: actionType,
      timestamp: new Date().toISOString(),
      sequence: this.getNextSequenceNumber(),
      playerId: this.playerId,
      data: {
        targetX: pointer.x,
        targetY: pointer.y,
        button: pointer.button
      }
    };

    this.sendUserAction(mouseAction);
  }

  // 🔧 Input System Helper Methods (Task 1.2.3 Refactor)
  
  private shouldProcessInput(): boolean {
    const currentTime = Date.now();
    const timeSinceLastInput = currentTime - this.lastInputTime;
    
    if (timeSinceLastInput < BattleScene.INPUT_DEBOUNCE_DELAY) {
      return false;
    }
    
    this.lastInputTime = currentTime;
    return true;
  }

  private getNextSequenceNumber(): number {
    return ++this.actionSequence;
  }

  public sendUserAction(action: UserAction): void {
    // Suppress in backend-engine test mode to avoid noisy USER_ACTION logs
    try { if (String(webSocketService.getConfig().url||'').includes(':8081')) return; } catch {}
    // Validate action data
    if (!this.validateActionData(action)) {
      console.error('Invalid action data:', action);
      return;
    }

    // Send through WebSocketService with error handling
    try {
      if (webSocketService.isConnected()) {
        webSocketService.sendMessage('USER_ACTION', action);
        console.log('📤 Sent user action via WebSocketService:', action);
      } else {
        console.warn('Cannot send action: WebSocketService not connected');
      }
    } catch (error) {
      console.error('Failed to send user action:', error);
    }
  }

  public validateActionData(action: any): boolean {
    // Type check
    if (!action || typeof action !== 'object') {
      console.warn('Action validation failed: Action must be an object');
      return false;
    }
    
    // Check required fields
    const requiredFields: (keyof UserAction)[] = ['type', 'timestamp', 'sequence', 'playerId', 'data'];
    for (const field of requiredFields) {
      if (!(field in action)) {
        console.warn(`Action validation failed: Missing required field: ${field}`);
        return false;
      }
    }

    // Validate action type
    if (!BattleScene.VALID_ACTION_TYPES.includes(action.type)) {
      console.warn(`Action validation failed: Invalid action type: ${action.type}`);
      return false;
    }

    // Validate timestamp format
    if (isNaN(Date.parse(action.timestamp))) {
      console.warn('Action validation failed: Invalid timestamp format');
      return false;
    }

    // Validate sequence number
    if (!Number.isInteger(action.sequence) || action.sequence < 0) {
      console.warn('Action validation failed: Sequence must be a non-negative integer');
      return false;
    }

    return true;
  }

  public isActive(): boolean {
    // Check if scene is active and ready for input
    return this.scene.isActive() && this.isWebSocketConnected();
  }

  // 🗺️ Map Configuration Methods (Phase 2.1 Integration)
  
  // Basic Configuration Methods
  public getDefaultMapSize(): MapSize { return { ...DEFAULT_MAP_SIZE }; }
  public getMaxMapSize(): MapSize { return { ...MAX_MAP_SIZE }; }
  public getTileSize(): number { return TILE_SIZE; }
  
  // Terrain Configuration Methods  
  public isTerrainPassable(terrain: TerrainType): boolean { return TERRAIN_PASSABLE[terrain]; }
  public getTerrainMovementCost(terrain: TerrainType): number { return TERRAIN_MOVEMENT_COSTS[terrain]; }
  
  // Map Generation Configuration
  public getDefaultMapGenerationOptions(): MapGenerationOptions { return { ...DEFAULT_MAP_GENERATION_OPTIONS }; }
  
  public validateMapGenerationOptions(options: Partial<MapGenerationOptions>): MapGenerationOptions {
    return {
      width: Math.min(options.width || DEFAULT_MAP_GENERATION_OPTIONS.width, MAX_MAP_SIZE.width),
      height: Math.min(options.height || DEFAULT_MAP_GENERATION_OPTIONS.height, MAX_MAP_SIZE.height),
      seed: options.seed,
      ...this.mergeGenerationDefaults(options)
    };
  }
  
  private mergeGenerationDefaults(options: Partial<MapGenerationOptions>) {
    const defaults = DEFAULT_MAP_GENERATION_OPTIONS;
    return {
      waterRange: options.waterRange || defaults.waterRange,
      mountainRange: options.mountainRange || defaults.mountainRange,
      forestRange: options.forestRange || defaults.forestRange,
      buildingClusters: options.buildingClusters || defaults.buildingClusters,
      housesPerCluster: options.housesPerCluster || defaults.housesPerCluster,
      nearWaterDist: options.nearWaterDist || defaults.nearWaterDist,
      passableRatioMin: options.passableRatioMin || defaults.passableRatioMin
    };
  }
  
  // Sprite Compatibility Information
  public getSlimeCompatibilityInfo(): {
    slimeSize: { width: number; height: number };
    tileSize: number;
    anchor: { x: number; y: number };
    tilesSpanned: { width: number; height: number };
  } {
    const slimeSize = { width: 65, height: 64 };
    return {
      slimeSize,
      tileSize: TILE_SIZE,
      anchor: { x: 0.5, y: 0.9 },
      tilesSpanned: {
        width: Math.ceil(slimeSize.width / TILE_SIZE),
        height: Math.ceil(slimeSize.height / TILE_SIZE)
      }
    };
  }
  
  // 🌸 Seasonal Asset Methods (Phase 2.2 Integration)
  // Removed SeasonalAssetManager integration - methods deleted due to missing dependencies

  // ==================== Phase 2.3: PhaserAnimationManager Integration Methods ====================
  
  // Agent animation methods - delegate to PhaserAnimationManager
  public playAgentAnimation(agentId: string, animationType: string): boolean {
    if (!this.phaserAnimationManager) return false;
    return this.phaserAnimationManager.playAgentAnimation(agentId, animationType as any);
  }

  public playAgentAnimationWithDirection(agentId: string, animationType: string, direction: string): boolean {
    if (!this.phaserAnimationManager) return false;
    return this.phaserAnimationManager.playAgentAnimationWithDirection(agentId, animationType as any, direction as any);
  }

  public chainAnimations(agentId: string, animations: string[]): boolean {
    if (!this.phaserAnimationManager) return false;
    return this.phaserAnimationManager.chainAnimations(agentId, animations as any[]);
  }

  public addAgentWithWeapon(agentId: string, x: number, y: number, weaponType: string): any {
    if (!this.phaserAnimationManager) return null;
    return this.phaserAnimationManager.addAgentWithWeapon(agentId, x, y, weaponType as any);
  }

  // Game event handling
  /**
   * @deprecated 禁止直接使用动画事件处理，请通过Engine.handle()路由
   */
  public handleGameEvent(eventType: string, eventData: any): void {
    console.warn('🚨 Deprecated API: handleGameEvent - 请通过Engine.handle()路由游戏事件');
    // Implementation removed - use Engine.handle() instead
  }

  /**
   * @deprecated 禁止直接访问动画映射，请通过Engine获取统一映射
   */
  public getAnimationMapping(eventType: string): string | undefined {
    console.warn('🚨 Deprecated API: getAnimationMapping - 请通过Engine获取统一动画映射');
    // Implementation removed - use Engine routing instead
    return undefined;
  }

  // Animation controls
  /**
   * @deprecated 禁止直接使用动画API，请通过Engine.handle()路由
   */
  public pauseAgentAnimation(agentId: string): void {
    console.warn('🚨 Deprecated API: pauseAgentAnimation - 请通过Engine.handle()路由动画控制');
    if (!this.phaserAnimationManager) return;
    this.phaserAnimationManager.pauseAgentAnimation(agentId);
  }

  /**
   * @deprecated 禁止直接使用动画API，请通过Engine.handle()路由
   */
  public resumeAgentAnimation(agentId: string): void {
    console.warn('🚨 Deprecated API: resumeAgentAnimation - 请通过Engine.handle()路由动画控制');
    if (!this.phaserAnimationManager) return;
    this.phaserAnimationManager.resumeAgentAnimation(agentId);
  }

  /**
   * @deprecated 禁止直接使用动画API，请通过Engine.handle()路由
   */
  public stopAgentAnimation(agentId: string): void {
    console.warn('🚨 Deprecated API: stopAgentAnimation - 请通过Engine.handle()路由动画控制');
    if (!this.phaserAnimationManager) return;
    this.phaserAnimationManager.stopAgentAnimation(agentId);
  }

  /**
   * @deprecated 禁止直接访问动画性能指标，请通过Engine获取统一指标
   */
  public getAnimationPerformanceMetrics(): any {
    console.warn('🚨 Deprecated API: getAnimationPerformanceMetrics - 请通过Engine获取统一性能指标');
    if (!this.phaserAnimationManager) return {};
    return this.phaserAnimationManager.getAnimationPerformanceMetrics();
  }

  /**
   * @deprecated 禁止直接访问CombatSequencer，请通过Engine路由战斗动画
   */
  public getCombatSequencer(): PhaserCombatSequencer | null {
    console.warn('🚨 Deprecated API: getCombatSequencer - 请通过Engine路由战斗动画');
    return this.phaserCombatSequencer;
  }

  // 旧式角色帧加载与命名工具已移除，统一走 SpriteAssetLoader 管道

  private loadWeaponAssets(): void {
    // console.log('⚔️ 加载武器资源 (3种武器)'); // 注释掉武器资源加载日志
    const weapons = [
      { type: 'axe', file: 'Axe.png' },
      { type: 'sword', file: 'Sword.png' },
      { type: 'lance', file: 'Lance.png' }
    ];
    
    weapons.forEach(weapon => {
      const key = `resource-${weapon.type}`;
      const path = `/assets/weapons/${weapon.file}`; // use absolute public path
      this.load.image(key, path);
    });
  }

  // 🔥 Phase B.1: 季节性地图素材加载实现 (基于SeasonalAssetManager业务逻辑)
  private loadTilemapAssets(): void {
    // console.log('🧩 加载静态tilemap素材(assets/tilemap)'); // 注释掉tilemap素材加载日志
    const basePath = '/assets/tilemap';
    const baseTiles = [
      'PLAIN',
      'WATER_FULL', 'WATER_EDGE_TOP', 'WATER_EDGE_BOTTOM', 'WATER_EDGE_LEFT', 'WATER_EDGE_RIGHT',
      'WATER_CORNER_TOP_LEFT', 'WATER_CORNER_TOP_RIGHT', 'WATER_CORNER_BOTTOM_LEFT', 'WATER_CORNER_BOTTOM_RIGHT',
      'WATER_INNER_TOP_LEFT', 'WATER_INNER_TOP_RIGHT', 'WATER_INNER_BOTTOM_LEFT', 'WATER_INNER_BOTTOM_RIGHT',
      'SAND_FULL', 'SAND_EDGE_TOP', 'SAND_EDGE_BOTTOM', 'SAND_EDGE_LEFT', 'SAND_EDGE_RIGHT',
      'SAND_CORNER_TOP_LEFT', 'SAND_CORNER_TOP_RIGHT', 'SAND_CORNER_BOTTOM_LEFT', 'SAND_CORNER_BOTTOM_RIGHT',
      'SAND_INNER_TOP_LEFT', 'SAND_INNER_TOP_RIGHT', 'SAND_INNER_BOTTOM_LEFT', 'SAND_INNER_BOTTOM_RIGHT'
    ];
    const overlays = [ 'FOREST01', 'FOREST02', 'MOUNTAIN', 'BUILDING01', 'BUILDING02', 'FENCE', 'FLOWER' ];
    baseTiles.forEach(key => {
      this.load.image(key, `${basePath}/${key}.png`);
    });
    const aliasPath: Record<string,string> = {
      BUILDING01: 'building01.png',
      BUILDING02: 'building02.png',
      FENCE: 'fence.png'
    };
    overlays.forEach(key => {
      const path = aliasPath[key] ? `${basePath}/${aliasPath[key]}` : `${basePath}/${key}.png`;
      this.load.image(key, path);
    });

    // ☁️ 加载云层素材
    this.load.image('CLOUD01', `${basePath}/CLOUD01.png`);
    this.load.image('CLOUD02', `${basePath}/CLOUD02.png`);

    // 🐉 加载飞龙动画帧素材
    this.load.image('DRAGON_1', `${basePath}/DRAGON_1.png`);
    this.load.image('DRAGON_2', `${basePath}/DRAGON_2.png`);
    this.load.image('DRAGON_3', `${basePath}/DRAGON_3.png`);
  }

  // 🔥 创建fallback纹理用于加载失败的资源
  private createFallbackTexture(key: string): void {
    try {
      // 创建一个简单的彩色方块作为fallback
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // 根据不同类型使用不同颜色
        let color = '#FF6B6B'; // 默认红色表示错误
        if (key.includes('WATER')) color = '#4ECDC4';
        else if (key.includes('SAND')) color = '#F7DC6F';
        else if (key.includes('PLAIN')) color = '#82E0AA';
        else if (key.includes('FOREST')) color = '#58D68D';
        else if (key.includes('MOUNTAIN')) color = '#AEB6BF';

        ctx.fillStyle = color;
        ctx.fillRect(0, 0, 64, 64);

        // 添加纹理标识
        ctx.fillStyle = '#FFFFFF';
        ctx.font = '8px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('MISSING', 32, 32);
        ctx.fillText(key.substring(0, 8), 32, 42);

        // 将canvas转换为Phaser纹理
        this.textures.addCanvas(key, canvas);
        console.log(`✅ 已为 ${key} 创建fallback纹理`);
      }
    } catch (error) {
      console.error(`❌ 无法创建fallback纹理 ${key}:`, error);
    }
  }

  private loadResourceAssets(): void {
    // 医疗资源
    const medicals = [
      { type: 'large-medkit', file: 'Large Medkit.png' },
      { type: 'small-medkit', file: 'Small Medkit.png' }
    ];
    
    medicals.forEach(medical => {
      const key = `resource-${medical.type}`;
      const path = `/assets/medicals/${medical.file}`; // use absolute public path
      this.load.image(key, path);
    });
  }



  // ==================== 🔥 Phase C: Resource System Methods ====================

  /**
   * 处理资源生成事件
   */
  private handleResourceSpawnedEvent(data: any): void {
    if (!data.newItems || !Array.isArray(data.newItems)) return;
    // 仅在 __viz_internal 或 __snapshot 下执行可视化
    if (!data.__viz_internal && !data.__snapshot) return;

    const isSnapshot = Boolean(data.__snapshot);
    const filteredItems = data.newItems.filter((item: any) => {
      if (!item?.itemId) return false;

      if (!isSnapshot && this.snapshotResourceIds.has(item.itemId)) {
        // Skip duplicate spawned from snapshot and flush queue
        this.snapshotResourceIds.delete(item.itemId);
        return false;
      }

      if (isSnapshot) {
        this.snapshotResourceIds.add(item.itemId);
      }

      return true;
    });

    if (filteredItems.length === 0) {
      return;
    }

    // 🆕 Use ResourceSystem for resource management (v3.0.79+)
    if (this.resourceSystem) {
      this.resourceSystem.handleResourceSpawned({ newItems: filteredItems });
    } else {
      // Fallback to legacy method if ResourceSystem not available
      filteredItems.forEach((item: any) => {
        if (item.position && item.itemId) {
          const resourceType = this.getResourceKey(item.category, item.type) || 'resource-sword';
          this.createResourceSprite(item.itemId, resourceType, item.position);
        }
      });
    }
    
  }

  /**
   * 处理权威快照（system:stateSync）
   */
  private handleSystemStateSync(snapshot: any): void {
    if (!snapshot || typeof snapshot !== 'object') {
      console.warn('BattleScene: Received invalid system:stateSync payload');
      return;
    }

    // 探针：确认后端已开启通道（收到权威快照）
    const roomId = this.registry.get('roomId');
    console.log(`🟢 握手确认：收到 system:stateSync（通道已打开）| roomId=${roomId}`);
    this.registry.set('channelOpen', true);
    console.log('🧭 BattleScene: Applying authority snapshot');

    this.awaitingServerSnapshot = false;
    // RUNNING 期差量化：若已应用过快照且处于 RUNNING，不重建地图，仅差量应用
    const currentPhase = this.registry.get('gamePhase');
    const incrementalOnly = !!this.hasAppliedSnapshot && currentPhase === 'RUNNING';
    this.hasAppliedSnapshot = true;

    if (this.fallbackTimer) {
          this.fallbackTimer.remove();
          this.fallbackTimer = null;
          }

    // 地图（权威）
    if (!incrementalOnly) {
      if (snapshot.map?.terrainMap && Array.isArray(snapshot.map.terrainMap) && snapshot.map.terrainMap.length > 0) {
        this.serverTerrainGrid = snapshot.map.terrainMap;
        this.mapHeight = snapshot.map.mapSize?.height ?? this.serverTerrainGrid.length;
        this.mapWidth = snapshot.map.mapSize?.width ?? (this.serverTerrainGrid[0]?.length ?? this.mapWidth);
        this._terrain = this._terrain || this.add.group();
        this._terrain.clear(true, true);
        this.initializeGridMap();
      }
    } else {
      console.log('🧩 [BattleScene] RUNNING snapshot: apply diff only (skip map rebuild)');
    }

    // Agents
    if (Array.isArray(snapshot.agents)) {
      this.applySnapshotAgents(snapshot.agents);
      this.registry.set('aliveAgents', snapshot.agents.length);
    }

    // Resources
    if (Array.isArray(snapshot.resources)) {
      this.applySnapshotResources(snapshot.resources);
    }

    // Safe zone
    if (snapshot.safeZone) {
      this.applySnapshotSafeZone(snapshot.safeZone);
    }

    // Turn info
    if (snapshot.turn?.current !== undefined) {
      this.registry.set('currentTurn', snapshot.turn.current);
    }
  }

  private applySnapshotAgents(agents: any[]): void {
    agents.forEach((agent, index) => {
      const agentId = agent.id || `agent_snapshot_${index}`;
      this.knownAgents.add(agentId);

      // v3.2.169: Only emit snapshot-born spawn if authoritative color is present
      // Otherwise, wait for realtime AGENT_SPAWNED (bridge normalizes to agent.color)
      let authoritativeColor = agent.color; // backend now supplies this; may be undefined in older snapshots
      if (authoritativeColor) authoritativeColor = normalizeColor(String(authoritativeColor));
      if (!authoritativeColor) {
        // Mark as known; defer spawn to realtime event with proper color
        return;
      }

      const spawnData = {
        agent: {
          id: agentId,
          position: agent.position || { x: 0, y: 0 },
          color: authoritativeColor,
          health: {
            current: agent.health ?? agent.health?.current ?? agent.maxHealth ?? 100,
            max: agent.maxHealth ?? agent.health?.max ?? 100
          },
          name: agent.name || agentId,
          status: agent.status || 'active'
        },
        __snapshot: true
      };

      try {
        this.handleAgentSpawnedEvent(spawnData);
      } catch (error) {
        console.warn('BattleScene: Failed to apply snapshot agent', agentId, error);
      }
    });
  }

  private applySnapshotResources(resources: any[]): void {
    if (!this.resourceSystem) {
      this.resourceSystem = new ResourceSystem(this);
    }
    this.resourceSystem.cleanup();

    const normalized = resources
      .map((resource: any) => ({
        itemId: resource.itemId || resource.id,
        type: (resource.type || 'WEAPON').toUpperCase(),
        category: (resource.category || 'weapon').toLowerCase(),
        weaponType: resource.weaponType ? String(resource.weaponType).toUpperCase() : undefined,
        medicalType: resource.medicalType ? String(resource.medicalType).toLowerCase() : undefined,
        position: resource.position || { x: 0, y: 0 }
      }))
      .filter(item => !!item.itemId);

    this.handleResourceSpawnedEvent({ newItems: normalized, __snapshot: true });
  }

  private applySnapshotSafeZone(safeZone: any): void {
    const center = {
      x: safeZone.center?.x ?? safeZone.centerX ?? safeZone.currentCenter?.x ?? 25,
      y: safeZone.center?.y ?? safeZone.centerY ?? safeZone.currentCenter?.y ?? 25
    };
    const radius = safeZone.currentRadius ?? safeZone.radius ?? safeZone.nextRadius ?? 20;
    this.updateSafeZone(center.x, center.y, radius);
  }

  /**
   * 处理资源收集事件
   */
  private handleResourceCollectedEvent(data: any): void {
    if (!data.itemId) return;
    // 可视化仅在 __viz_internal 时执行
    if (!data.__viz_internal && !data.__snapshot) return;
    
    // 🆕 Use ResourceSystem for resource management (v3.0.79+)
    if (this.resourceSystem) {
      this.resourceSystem.handleResourceCollected(data);
    }
    // Route to engine (weapon → atk; medical → no char animation)
    try { this.animationEngine?.handle({ type: EventTypes.RESOURCE_COLLECTED, agentId: data.agentId, data }); } catch {}
  }

  /**
   * 处理武器装备事件
   */
  private handleWeaponEquippedEvent(data: any): void {
    if (!data.agentId) return;
    
    // 🆕 Use ResourceSystem for event handling (v3.0.79+)
    if (this.resourceSystem) {
      this.resourceSystem.handleWeaponEquipped(data);
    }
    
    const weaponType = data.weaponType || data.weapon?.weaponType || data.weapon?.type || 'SWORD';
    // Route to engine (equip → handled exclusively by Engine with move barrier)
    try { this.animationEngine?.handle({ type: EventTypes.WEAPON_EQUIPPED, agentId: data.agentId, data: { weaponType } }); } catch {}
  }

  // 在场景中播放“角色+武器”分层攻击动画，附着到Agent容器
  private playInWorldWeaponCollectAtk(agentId: string, weaponType: string): void {
    try {
      if (!this.inWorldAnimationPlayer || !this.phaserAnimationManager) return;
      const container = this.phaserAnimationManager.agentContainers.get(agentId);
      const color = (this.phaserAnimationManager as any).agentColors?.get(agentId) || 'green';
      if (!container) return;
      // Align to the base character sprite local position inside the container
      let pos = { x: 0, y: 0 };
      try {
        const baseSprite = (this.phaserAnimationManager as any).agentSprites?.get(agentId);
        if (baseSprite && typeof baseSprite.x === 'number' && typeof baseSprite.y === 'number') {
          pos = { x: baseSprite.x, y: baseSprite.y };
          // 隐藏基础精灵，避免分层特效与基础帧重影
          try { baseSprite.setVisible(false); } catch {}
        }
      } catch {}
      const charType = this.mapColorToCharacterType(String(color));
      const weapType = this.mapWeaponString(String(weaponType));
      this.inWorldAnimationPlayer.playLayeredAnimation(
        `${agentId}#collect`,
        charType,
        weapType,
        AnimationType.ATTACK,
        pos,
        { loop: false, fps: 15, parentContainer: container, scale: 1.3, onComplete: () => {
          try { this.inWorldAnimationPlayer?.stopAnimation(`${agentId}#collect`); } catch {}
          // 收集特效结束后，恢复基础精灵可见（如随后收到 WEAPON_EQUIPPED，将在装备处理处再次切换为分层）
          try {
            const baseSprite = (this.phaserAnimationManager as any).agentSprites?.get(agentId);
            if (baseSprite) baseSprite.setVisible(true);
          } catch {}
        }}
      );
    } catch (e) {
      console.warn('playInWorldWeaponCollectAtk failed:', (e as Error).message);
    }
  }

  private mapColorToCharacterType(color: string): CharacterType {
    // 统一转由工具函数处理，避免多处维护
    try { return (require('../animation/utils/spriteKeyUtils') as any).colorToCharacterType(String(color)); } catch { return CharacterType.GREEN; }
  }

  private mapWeaponString(type: string): WeaponType {
    const t = type.toUpperCase();
    if (t === 'AXE') return WeaponType.AXE;
    if (t === 'LANCE') return WeaponType.LANCE;
    return WeaponType.SWORD;
  }

  /**
   * 处理武器破损事件
   */
  private handleWeaponBrokenEvent(data: any): void {
    if (!data.agentId || !data.weapon) return;
    
    // 🆕 Use ResourceSystem for event handling (v3.0.79+)
    if (this.resourceSystem) {
      this.resourceSystem.handleWeaponBroken(data);
    }
    
    // 暂时留空，破损动画暂不实现
    console.log(`💔 Agent ${data.agentId} weapon ${data.weapon.name} broken`);
    // 全局修正：武器破损后恢复为无武器动画
    try { this.phaserAnimationManager?.clearEquippedWeapon(data.agentId); } catch {}
  }

  // 🔧 迁移：handleItemUsedEvent已迁移到Engine系统，此方法已删除

  /**
   * 处理物品丢弃事件
   */
  private handleItemDroppedEvent(data: any): void {
    if (!data.itemId || !data.dropPosition) return;
    
    // 在地面创建新的资源点
    const category = data.item?.category || 'material';
    const type = data.item?.type || 'UNKNOWN';
    const resourceType = this.getResourceKey(category, type) || 'resource-sword';
    
    this.createResourceSprite(data.itemId, resourceType, data.dropPosition);
    // 全局修正：若丢弃的是当前装备的武器，恢复为无武器动画
    try {
      const cat = String(data.item?.category || '').toLowerCase();
      if (cat === 'weapon' && data.agentId) {
        this.phaserAnimationManager?.clearEquippedWeapon(String(data.agentId));
      }
    } catch {}
  }


  /**
   * 播放武器收集动画
   */
  // 🔧 迁移：playWeaponCollectionAnimation已迁移到Engine/SceneAdapter系统，此方法已删除

  /**
   * 播放武器装备动画
   */
  // 🔧 迁移：playWeaponEquipAnimation已迁移到Engine/SceneAdapter系统，此方法已删除

  /**
   * 播放物品使用动画
   */
  // 🔧 迁移：playItemUseAnimation已迁移到Engine/SceneAdapter系统，此方法已删除

  /**
   * 获取资源键名
   */
  private getResourceKey(category: string, type: string): string | null {
    const cat = String(category).toLowerCase();
    switch (cat) {
      case 'weapon':
        return this.getWeaponResourceKey(type);
      case 'consumable':
      case 'medical': // 🔧 支持 'MEDICAL' category
        return this.getMedicalResourceKey(type);
      default:
        return null;
    }
  }

  /**
   * 获取武器资源键名
   */
  public getWeaponResourceKey(weaponType: string): string {
    const weaponMapping: Record<string, string> = {
      'SWORD': 'resource-sword',
      'AXE': 'resource-axe', 
      'LANCE': 'resource-lance',
      'sword': 'resource-sword',
      'axe': 'resource-axe',
      'lance': 'resource-lance'
    };
    return weaponMapping[weaponType] || 'resource-sword';
  }

  /**
   * 获取医疗资源键名
   */
  public getMedicalResourceKey(medicalType: string): string {
    const medicalMapping: Record<string, string> = {
      'large': 'resource-large-medkit',
      'small': 'resource-small-medkit',
      'LARGE': 'resource-large-medkit',
      'SMALL': 'resource-small-medkit'
    };
    return medicalMapping[medicalType] || 'resource-large-medkit';
  }

  // 🧪 Agent动画系统测试方法 (Phase 5: Integration Testing)

  /**
   * 测试Agent移动动画 - 单元格跳跃
   */
  public async testAgentMovement(agentId: string = 'test-agent'): Promise<void> {
    console.log('🧪 Testing agent movement animation...');
    
    // 确保有测试agent
    if (!this.agents.has(agentId)) {
      this.addAgent(agentId, 5, 5);
    }

    // 测试移动序列: (5,5) → (7,5) → (7,8) → (5,8) → (5,5)
    const moveSequence = [
      { x: 7, y: 5 }, // 右移
      { x: 7, y: 8 }, // 下移  
      { x: 5, y: 8 }, // 左移
      { x: 5, y: 5 }  // 上移回起点
    ];

    for (const targetTile of moveSequence) {
      const currentAgent = this.agents.get(agentId);
      if (!currentAgent) continue;
      
      const currentTile = {
        x: Math.floor(currentAgent.x / TILE_SIZE),
        y: Math.floor(currentAgent.y / TILE_SIZE)
      };
      
      console.log(`🎮 Moving ${agentId} from (${currentTile.x},${currentTile.y}) to (${targetTile.x},${targetTile.y})`);
      await this.agentSystem?.moveAgentToTile(agentId, currentTile, targetTile);
      
      // 暂停一秒观察
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    console.log('✅ Agent movement test completed');
  }

  /**
   * 测试Agent战斗动画 - 面对面攻击
   */
  public async testAgentCombat(attackerId: string = 'attacker', defenderId: string = 'defender'): Promise<void> {
    console.log('🧪 Testing agent combat animation...');
    
    // 创建测试agents
    if (!this.agents.has(attackerId)) {
      this.addAgent(attackerId, 3, 3);
    }
    if (!this.agents.has(defenderId)) {
      this.addAgent(defenderId, 6, 6);
    }

    // 发起战斗：攻击者移动到防守者旁边
    console.log('🥊 Initiating combat...');
    await this.agentSystem?.initiateCombat(attackerId, defenderId);
    
    // 等待一秒观察面对面设置
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // 执行战斗序列：3轮轮流攻击
    console.log('⚔️ Executing combat sequence...');
    await this.agentSystem?.executeCombatSequence(attackerId, defenderId, 3);
    
    console.log('✅ Agent combat test completed');
  }

  /**
   * 测试资源收集动画 - 医疗vs武器
   */
  public async testResourceCollection(agentId: string = 'collector'): Promise<void> {
    console.log('🧪 Testing resource collection animations...');
    
    // 确保有测试agent
    if (!this.agents.has(agentId)) {
      this.addAgent(agentId, 10, 10);
    }

    // 测试医疗资源收集 (无动画)
    console.log('💊 Testing medical resource collection...');
    await this.agentSystem?.handleResourceCollection(agentId, 'MEDICAL');
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // 测试武器资源收集 (atk动画)
    console.log('⚔️ Testing weapon resource collection...');
    await this.agentSystem?.handleResourceCollection(agentId, 'WEAPON');
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('✅ Resource collection test completed');
  }

  /**
   * 验证尺寸对齐 - Agent与Tile完美匹配
   */
  public validateSizeAlignment(): void {
    console.log('📐 Validating size alignment...');
    console.log(`TILE_SIZE: ${TILE_SIZE}px`);
    console.log(`gridSize: ${this.gridSize}px`);
    console.log(`SLIME_SIZE: ${TILE_SIZE}x${TILE_SIZE}px`); // Agent size matches TILE_SIZE
    
    // 检查是否匹配
    const isMatched = this.gridSize === TILE_SIZE;
    console.log(`Size alignment: ${isMatched ? '✅ MATCHED' : '❌ MISMATCHED'}`);
    
    if (!isMatched) {
      console.warn(`⚠️ Size mismatch detected: gridSize=${this.gridSize} vs TILE_SIZE=${TILE_SIZE}`);
    }
  }

  // 🆕 WebSocketService重构 - 游戏事件处理方法
  private setupGameEventHandlers(): void {
    if (this.handlersRegistered) {
      console.warn('setupGameEventHandlers: handlers already registered, skip');
      return;
    }

    // 🆕 v3.2.146: Loading completion listener - triggers handshake when event bridge ready
    this.boundHandlers.GAME_LOADING_COMPLETED = this.handleGameLoadingCompleted.bind(this);
    webSocketService.onGameEvent('GAME_LOADING_COMPLETED', this.boundHandlers.GAME_LOADING_COMPLETED);

    // 游戏生命周期事件
    this.boundHandlers.GAME_STARTED = this.handleGameStarted.bind(this);
    this.boundHandlers.TURN_STARTED = this.handleTurnStarted.bind(this);
    this.boundHandlers.GAME_ENDED = this.handleGameEnded.bind(this);
    webSocketService.onGameEvent('GAME_STARTED', this.boundHandlers.GAME_STARTED);
    webSocketService.onGameEvent('TURN_STARTED', this.boundHandlers.TURN_STARTED);
    webSocketService.onGameEvent('GAME_ENDED', this.boundHandlers.GAME_ENDED);

    // Authority snapshot (system:stateSync)
    this.boundHandlers.SYSTEM_STATE_SYNC = this.handleSystemStateSync.bind(this);
    webSocketService.onGameEvent('SYSTEM_STATE_SYNC', this.boundHandlers.SYSTEM_STATE_SYNC);
    // 🆕 回合开始/结束：即时事件改为“状态-only”；仅在 __viz_internal=true（由回放驱动）时执行可视化
    this.boundHandlers.AGENT_TURN_STARTED = (d:any)=>{
      const aid = d?.agentId || d?.id;
      if (d && d.__viz_internal) {
        // 立即点亮箭头（不抢镜头）
        if (aid) this.showAgentTurnArrow(aid);
        this.awaitingHighlightAgentId = null;
        try { this.registry.set('activeAgentId', aid); } catch {}
      } else {
        try { this.registry.set('activeAgentId', aid); } catch {}
      }
    };
    this.boundHandlers.AGENT_TURN_ENDED = (d:any)=>{
      const aid = d?.agentId || d?.id;
      if (d && d.__viz_internal) {
        if (aid) this.hideAgentTurnArrow(aid);
        try { if (this.registry.get('autoFollowCamera')) this.cameras.main.stopFollow(); } catch {}
      }
      if (aid && this.awaitingHighlightAgentId === aid) {
        this.awaitingHighlightAgentId = null;
      }
    };
    webSocketService.onGameEvent('AGENT_TURN_STARTED', this.boundHandlers.AGENT_TURN_STARTED);
    webSocketService.onGameEvent('AGENT_TURN_ENDED', this.boundHandlers.AGENT_TURN_ENDED);
    
    // 现有游戏操作事件 (迁移自直接socket监听)
    this.boundHandlers.AGENT_SPAWNED = this.handleAgentSpawnedEvent.bind(this);
    this.boundHandlers.AGENT_MOVED = this.handleAgentMovedEvent.bind(this);
    this.boundHandlers.AGENT_ELIMINATED = this.handleAgentEliminatedEvent.bind(this);
    // 实时HP事件：通过闸门控制地图层刷新（剧场开启→缓存；剧场结束→flush）
    this.boundHandlers.AGENT_HEALTH_CHANGED = (d:any)=>{
      try {
        const h = d?.currentHealth || d?.health;
        const id = d?.agentId;
        if (id && h && typeof h.current === 'number' && typeof h.max === 'number') {
          if (this.activeCombatCount > 0) {
            this.cachedHpUpdates.set(id, { current: h.current, max: h.max });
          } else {
            this.updateAgentHealthBar(id, h);
          }
        }
      } catch {}
    };
    // COMBAT_* 路由改为由 CombatEventRouter 统一处理，移除 Engine 绑定
    this.boundHandlers.COMBAT_STARTED = null as any;
    webSocketService.onGameEvent('AGENT_SPAWNED', this.boundHandlers.AGENT_SPAWNED);
    webSocketService.onGameEvent('AGENT_MOVED', this.boundHandlers.AGENT_MOVED);
    webSocketService.onGameEvent('AGENT_ELIMINATED', this.boundHandlers.AGENT_ELIMINATED);
    webSocketService.onGameEvent('AGENT_HEALTH_CHANGED', this.boundHandlers.AGENT_HEALTH_CHANGED);
    // 解除对 COMBAT_* 的 Engine 绑定，由 CombatEventRouter 直接从 WebSocketService 监听
    this.boundHandlers.RESOURCE_SPAWNED = (d:any)=>{
      // 可视化仅接受 __viz_internal 或 __snapshot（权威快照）
      if (d?.__viz_internal || d?.__snapshot) return this.handleResourceSpawnedEvent(d);
      // 非可视化：可在此更新状态（略）
    };
    // 放开 RESOURCE_COLLECTED 可视化处理：收到后先行移除地面资源（WEAPON/医疗），RESOURCE_DESPAWNED 作为幂等兜底
    this.boundHandlers.RESOURCE_COLLECTED = (d:any)=>{
      try { this.handleResourceCollectedEvent(d); } catch {}
    };
    // 新增：资源消失（系统清扫/过期等）→ 直接状态移除，不走回放
    this.boundHandlers.RESOURCE_DESPAWNED = (d:any)=>{
      try {
        if (d?.itemId) {
          if (this.resourceSystem) this.resourceSystem.removeResource(d.itemId);
          this.removeResourceSprite(d.itemId);
        }
      } catch {}
    };
    this.boundHandlers.WEAPON_EQUIPPED = (d:any)=>{ if (d?.__viz_internal) return this.handleWeaponEquippedEvent(d); };
    webSocketService.onGameEvent('RESOURCE_SPAWNED', this.boundHandlers.RESOURCE_SPAWNED);
    webSocketService.onGameEvent('RESOURCE_COLLECTED', this.boundHandlers.RESOURCE_COLLECTED);
    webSocketService.onGameEvent('RESOURCE_DESPAWNED', this.boundHandlers.RESOURCE_DESPAWNED);
    webSocketService.onGameEvent('WEAPON_EQUIPPED', this.boundHandlers.WEAPON_EQUIPPED);
    // 持续持武器：监听破损/丢弃
    this.boundHandlers.WEAPON_BROKEN = this.handleWeaponBrokenEvent.bind(this);
    this.boundHandlers.ITEM_DROPPED = this.handleItemDroppedEvent.bind(this);
    webSocketService.onGameEvent('WEAPON_BROKEN', this.boundHandlers.WEAPON_BROKEN);
    webSocketService.onGameEvent('ITEM_DROPPED', this.boundHandlers.ITEM_DROPPED);
    
    // 其他动画事件
    const dispatchItemUse = (payload: any) => {
      const aid = payload?.agentId;
      if (!aid) return;
      this.lastItemUseTimestamp.set(aid, Date.now());
      this.animationEngine?.handle({ type: EventTypes.ITEM_USED, agentId: aid, data: payload });
    };
    this.boundHandlers.ITEM_USED = (d:any)=>dispatchItemUse(d);
    webSocketService.onGameEvent('ITEM_USED', this.boundHandlers.ITEM_USED);
    this.boundHandlers.ITEM_CONSUMED = (d:any)=>{
      const aid = d?.agentId;
      if (!aid) return;
      const last = this.lastItemUseTimestamp.get(aid) ?? 0;
      if (Date.now() - last < this.ITEM_USE_DEDUP_MS) return;
      dispatchItemUse(d);
    };
    webSocketService.onGameEvent('ITEM_CONSUMED', this.boundHandlers.ITEM_CONSUMED);

    // 🔥 SafeZone动画事件路由到Engine
    this.boundHandlers.SAFEZONE_WARNING = (d:any)=>{ if (d?.__viz_internal) this.animationEngine?.handle({ type: EventTypes.SAFEZONE_WARNING, agentId: 'system', data: d }); };
    this.boundHandlers.SAFEZONE_CONTRACTION_STARTED = (d:any)=>{ if (d?.__viz_internal) this.animationEngine?.handle({ type: EventTypes.SAFEZONE_CONTRACTION_STARTED, agentId: 'system', data: d }); };
    this.boundHandlers.SAFEZONE_CONTRACTION_COMPLETED = (d:any)=>{ if (d?.__viz_internal) this.animationEngine?.handle({ type: EventTypes.SAFEZONE_CONTRACTION_COMPLETED, agentId: 'system', data: d }); };
    this.boundHandlers.SAFEZONE_UPDATED = (d:any)=>{ if (d?.__viz_internal) this.animationEngine?.handle({ type: EventTypes.SAFEZONE_UPDATED, agentId: 'system', data: d }); };
    this.boundHandlers.SAFEZONE_DAMAGE_APPLIED = (d:any)=>this.animationEngine?.handle({ type: EventTypes.SAFEZONE_DAMAGE_APPLIED, agentId: d.agentId, data: d });
    this.boundHandlers.ENVIRONMENT_DAMAGE = (d:any)=>this.animationEngine?.handle({ type: EventTypes.ENVIRONMENT_DAMAGE, agentId: d.agentId, data: d });
    webSocketService.onGameEvent('SAFEZONE_WARNING', this.boundHandlers.SAFEZONE_WARNING);
    webSocketService.onGameEvent('SAFEZONE_CONTRACTION_STARTED', this.boundHandlers.SAFEZONE_CONTRACTION_STARTED);
    webSocketService.onGameEvent('SAFEZONE_CONTRACTION_COMPLETED', this.boundHandlers.SAFEZONE_CONTRACTION_COMPLETED);
    webSocketService.onGameEvent('SAFEZONE_UPDATED', this.boundHandlers.SAFEZONE_UPDATED);
    webSocketService.onGameEvent('SAFEZONE_DAMAGE_APPLIED', this.boundHandlers.SAFEZONE_DAMAGE_APPLIED);
    webSocketService.onGameEvent('ENVIRONMENT_DAMAGE', this.boundHandlers.ENVIRONMENT_DAMAGE);

    // 🗺️ 新增：消费后端权威地图
    this.boundHandlers.MAP_GENERATED = (d:any)=>{
      try {
        const grid = d?.terrainMap || d?.grid;
        if (Array.isArray(grid) && grid.length>0) {
          this.serverTerrainGrid = grid;
          this.mapHeight = grid.length;
          this.mapWidth = grid[0].length;
          if (this.fallbackTimer) {
          this.fallbackTimer.remove();
          this.fallbackTimer = null;
          }
          this.awaitingServerSnapshot = false;

          this._terrain = this._terrain || this.add.group();
          this._terrain.clear(true, true);
          this.initializeGridMap();
        }
      } catch (e) { console.warn('MAP_GENERATED handling failed:', (e as Error).message); }
    };
    webSocketService.onGameEvent('MAP_GENERATED', this.boundHandlers.MAP_GENERATED);

    // 👁️ P2.1: Spectator System
    this.boundHandlers.SPECTATOR_JOINED = (d:any)=>{
      try {
        const count = d?.spectatorCount;
        if (typeof count === 'number') {
          this.updateSpectatorCount(count);
        }
      } catch (e) { console.warn('SPECTATOR_JOINED handling failed:', (e as Error).message); }
    };
    this.boundHandlers.SPECTATOR_LEFT = (d:any)=>{
      try {
        const count = d?.spectatorCount;
        if (typeof count === 'number') {
          this.updateSpectatorCount(count);
        }
      } catch (e) { console.warn('SPECTATOR_LEFT handling failed:', (e as Error).message); }
    };
    webSocketService.onGameEvent('SPECTATOR_JOINED', this.boundHandlers.SPECTATOR_JOINED);
    webSocketService.onGameEvent('SPECTATOR_LEFT', this.boundHandlers.SPECTATOR_LEFT);

    // 设置cleanup监听器
    this.handlersRegistered = true;
    this.events.once('shutdown', this.cleanupWebSocketHandlers, this);

    // 绑定 Combat 可视化通路：统一使用 CombatEventRouter（回放/非回放均走同一路径）
    try {
      import('../animation/combat/CombatEventRouter').then(({ CombatEventRouter }) => {
        try {
          if (this.phaserAnimationManager && this.phaserCombatSequencer) {
            this.combatEventRouter = CombatEventRouter.getInstance(webSocketService as any, this.phaserCombatSequencer, this.phaserAnimationManager);
            // 统一总线模式：由 PlaybackScheduler 注入内部事件给 Router，Router 不直接绑定 WS
            this.combatEventRouter.bindExternal();
            window.addEventListener('ABR_COMBAT_STARTED' as any, this.onAbrCombatStarted as any);
            window.addEventListener('ABR_COMBAT_ENDED' as any, this.onAbrCombatEnded as any);
          }
        } catch (err) {
          console.warn('CombatEventRouter external bind failed:', (err as Error).message);
        }
      });
    } catch (e) { console.warn('Combat binding setup failed:', (e as Error).message); }
  }

  private onAbrCombatStarted = (_ev: any) => {
    this.activeCombatCount++;
  };
  private onAbrCombatEnded = (_ev: any) => {
    this.activeCombatCount = Math.max(0, this.activeCombatCount - 1);
    if (this.activeCombatCount === 0 && this.cachedHpUpdates.size > 0) {
      // flush 缓存到地图层
      try {
        this.cachedHpUpdates.forEach((h, id) => this.updateAgentHealthBar(id, h));
      } catch {}
      this.cachedHpUpdates.clear();
    }
  };

  private handleRegistryChange = (parent: any, key: string, data: any): void => {
    switch (key) {
      case 'gamePhase':
        this.updateGamePhaseUI(data);
        break;
      case 'currentTurn':
        this.updateTurnDisplay(data);
        break;
      case 'aliveAgents':
        this.updateAliveAgentsDisplay(data);
        break;
    }
  };

  /**
   * 🆕 Scene Handshake Protocol - 发送scene:ready握手信号
   * 在create()完成后调用，通知Backend前端已准备好接收gameplay events
   * Backend响应: system:stateSync → game:started → 开启gameplay通道
   */
  private sendSceneReadyHandshake(force: boolean = false): void {
    try {
      if ((this as any)._handshakeSent && !force) { return; }
      // 获取roomId (从Registry或sceneData)
      const roomId = this.registry.get('roomId');
      if (!roomId) {
        console.warn('⚠️ Cannot send scene:ready - roomId not found in registry');
        return;
      }

      console.log(`🤝 Sending scene:ready handshake for room ${roomId}`);
      webSocketService.sendSceneReady(roomId, 'BattleScene');
      (this as any)._handshakeSent = true;
    } catch (error) {
      console.error('❌ Failed to send scene:ready handshake:', error);
    }
  }

  // 🆕 v3.2.146: Loading completion handler - sends handshake after event bridge ready
  private handleGameLoadingCompleted(data: any): void {
    // 🔍 DEBUG: Enhanced loading completion logging
    console.log(`🎮 ========================================`);
    console.log(`✅ [BattleScene] Received game:loading:completed`);
    console.log(`   Data:`, data);
    console.log(`   Registry roomId:`, this.registry.get('roomId'));
    console.log(`🤝 [BattleScene] Event bridge ready, sending scene:ready handshake...`);
    console.log(`🎮 ========================================`);

    // 现在可以安全发送握手信号,Backend的event bridge已准备好接收
    this.sendSceneReadyHandshake();
  }

  // 🆕 游戏生命周期事件处理器
  private handleGameStarted(data: any): void {
    // 🔍 DEBUG: Enhanced game started logging
    console.log(`🚀 ========================================`);
    console.log(`🎮 [BattleScene] Game Started Event Received`);
    console.log(`   Data:`, data);
    console.log(`   Terrain Group Exists:`, !!this._terrain);
    console.log(`   Terrain Length:`, this._terrain?.getLength() || 0);
    console.log(`🗺️ [BattleScene] Initializing map rendering...`);
    console.log(`🚀 ========================================`);

    // 在游戏真正开始后生成地图
    try {
      if (this._terrain) {
        if (this._terrain.getLength() === 0) {
          this.initializeGridMap();
        }
      } else {
        this._terrain = this.add.group();
        this.initializeGridMap();
      }
      console.log(`✅ [BattleScene] Map initialization completed - ${this._terrain.getLength()} tiles rendered`);
    } catch (e) {
      console.error('❌ [BattleScene] handleGameStarted map init failed:', e);
    }
  }

  private handleTurnStarted(data: any): void {
    console.log('🔄 Turn Started:', data);
    // 在手动GFD模式下：首次回合开始再生成地图，确保顺序：connect → prepare/manual-start → step(回合1) → 地图/资源
    try {
      if (this._terrain) {
        if (this._terrain.getLength() === 0) {
          this.initializeGridMap();
        }
      } else {
        this._terrain = this.add.group();
        this.initializeGridMap();
      }
    } catch (e) {
      console.warn('BattleScene: handleTurnStarted map init failed:', e);
    }
  }

  private handleGameEnded(data: any): void {
    console.log('🏁 Game Ended:', data);
    try {
      if (this.phaserCombatSequencer?.isTheaterActive()) {
        this.phaserCombatSequencer.forceCloseActiveCombat('game_end');
      }
    } catch (e) {
      console.warn('Force close combat on game end failed:', e);
    }
    // 可以在这里添加游戏结束的特殊处理
  }

  // 🆕 现有事件的适配器方法
  private handleAgentSpawnedEvent(data: any): void {
    // 资产门闸：资产未就绪前缓存动画事件，避免“缺帧/动画不存在”
    if (!this.sceneAssetsReady) {
      this.deferredGameplayEvents.push({ type: 'AGENT_SPAWNED', data });
      return;
    }
    // 🔍 DEBUG: Enhanced agent spawned logging
    console.log(`👤 ========================================`);
    console.log(`🎯 [BattleScene] AGENT_SPAWNED event received`);
    console.log(`   Agent ID: ${data?.agent?.id}`);
    console.log(`   Position: (${data?.agent?.position?.x}, ${data?.agent?.position?.y})`);
    console.log(`   Color: ${data?.agent?.color || 'default'}`);
    console.log(`   Is Snapshot: ${Boolean((data as any).__snapshot)}`);
    console.log(`👤 ========================================`);

    if (data?.agent?.id && data?.agent?.position) {
      // 🔧 迁移：完全通过Engine处理（移除AgentSystem直接渲染路径，避免重复与占位渲染）
      // 之前的双重处理：BattleScene.addAgent() + Engine.SceneAdapter.idle()
      // 现在统一路由到Engine，由SceneAdapter.idle()处理Agent创建
      try {
        const { id, position } = data.agent;
        // v3.2.169: normalize color (emoji/name) to canonical color name
        let color = normalizeColor(String((data.agent as any).color || ''));
        const isSnapshot = Boolean((data as any).__snapshot);

        if (!isSnapshot && this.snapshotAgents.has(id)) {
          console.log(`♻️ BattleScene: Skip duplicate AGENT_SPAWNED for ${id} (flushed from snapshot queue)`);
          this.snapshotAgents.delete(id);
          return;
        }

        if (isSnapshot) {
          this.snapshotAgents.add(id);
        } else {
          this.snapshotAgents.delete(id);
        }
        this.knownAgents.add(id);
        console.log(`🎯 BattleScene: 路由AGENT_SPAWNED到Engine → ${id} @ grid(${position.x}, ${position.y}) color: ${color || 'default'}`);
        // 统一通过Engine处理Agent创建和动画，传递完整的agent数据包括颜色
        this.animationEngine?.handle({
          type: EventTypes.AGENT_SPAWNED,
          agentId: id,
          data: {
            position,
            color, // 已标准化的颜色名
            agent: data.agent, // 传递完整agent数据
            __snapshot: isSnapshot
          }
        });
        console.log(`✅ [BattleScene] Agent ${id} routed to Engine successfully`);
      } catch (e) {
        console.error(`❌ [BattleScene] route AGENT_SPAWNED to Engine failed:`, e);
      }
    }
  }

  private handleAgentMovedEvent(data: any): void {
    // 资产门闸：资产未就绪前缓存动画事件
    if (!this.sceneAssetsReady) {
      this.deferredGameplayEvents.push({ type: 'AGENT_MOVED', data });
      return;
    }
    if (data && data.__viz_internal && data.agentId && this.awaitingHighlightAgentId === data.agentId) {
      this.activateAgentHighlight(data.agentId);
    }
    const targetPosition = data.position || data.toPosition;
    // 仅在 __viz_internal（回放驱动）时执行可视化动画
    if (data && data.__viz_internal) {
      if (data.agentId && targetPosition) {
        console.log(`🎯 [VIZ] AGENT_MOVED via PlaybackScheduler → ${data.agentId} → (${targetPosition.x}, ${targetPosition.y})`);
        try {
          this.animationEngine?.handle({
            type: EventTypes.AGENT_MOVED,
            agentId: data.agentId,
            data: { position: targetPosition, toPosition: targetPosition }
          });
        } catch {}
      } else {
        console.warn(`⚠️ BattleScene: Invalid AGENT_MOVED event data:`, data);
      }
      return;
    }
    // 非可视化路径：仅记录最新目标位置（不触发动画），供其他UI/调试使用
    try {
      if (data?.agentId && targetPosition) {
        const key = `agent:${data.agentId}:lastTarget`;
        this.registry.set(key, { x: targetPosition.x, y: targetPosition.y });
      }
    } catch {}
  }

  // Flush queued gameplay events in order of arrival once assets are ready
  private flushDeferredGameplayEvents(): void {
    if (this.processingDeferred) return;
    this.processingDeferred = true;
    try {
      const queue = this.deferredGameplayEvents.splice(0);
      for (const evt of queue) {
        try {
          switch (evt.type) {
            case 'AGENT_SPAWNED':
              this.handleAgentSpawnedEvent(evt.data);
              break;
            case 'AGENT_MOVED':
              this.handleAgentMovedEvent(evt.data);
              break;
            default:
              // 其他动画事件可按需补充
              break;
          }
        } catch (e) {
          console.warn(`Deferred event ${evt.type} failed:`, (e as Error).message);
        }
      }
    } finally {
      this.processingDeferred = false;
    }
  }

  private handleAgentEliminatedEvent(data: any): void {
    if (!data?.agentId) return;
    const isVizEvent = !!data.__viz_internal;
    console.log(`💀 BattleScene: Agent eliminated event received for ${data.agentId}${isVizEvent ? '' : ' (non-visual)'}`);

    // 非可视化事件：仅记录/等待 PlaybackScheduler 注入的 __viz_internal 版本，避免绕过闸门
    if (!isVizEvent) {
      return;
    }

    // 清理回合箭头（确保唯一性）
    try { this.hideAgentTurnArrow(data.agentId); } catch {}
    // Route to animation engine for death animation（仅可视化路径）
    try {
      this.animationEngine?.handle({
        type: EventTypes.AGENT_ELIMINATED,
        agentId: data.agentId,
        data
      });
    } catch (e) {
      console.warn('Failed to handle agent elimination animation:', e);
    }
    // 不在此处立即移除，等死亡动画播放完毕后由 PMA 回调统一清理；增加兜底定时清理
    try {
      const aid = String(data.agentId);
      this.time.delayedCall(1500, () => {
        try {
          const still = (this.phaserAnimationManager as any)?.agentContainers?.get(aid) || this.agents.get(aid);
          if (still) {
            (this.phaserAnimationManager as any)?.removeAgent?.(aid);
            this.removeAgentSprite?.(aid);
          }
        } catch {}
      });
    } catch {}
  }

  private handleCombatStartedEvent(data: any): void {
    // Engine routes to Sequencer via CombatAdapter
    try { this.animationEngine?.handle({ type: EventTypes.COMBAT_STARTED, agentId: data.attackerId || data.agentId || 'unknown', data }); } catch {}
  }

  // 🆕 UI更新方法
  private updateGamePhaseUI(phase: string): void {
    console.log(`🎮 Game Phase Updated: ${phase}`);
    // 可以在这里更新UI显示游戏阶段
  }

  private updateTurnDisplay(turn: number): void {
    console.log(`🔄 Turn Display Updated: ${turn}`);
    // 可以在这里更新UI显示当前回合
  }

  private updateAliveAgentsDisplay(count: number): void {
    console.log(`👥 Alive Agents Updated: ${count}`);
    // 可以在这里更新UI显示存活Agent数量
  }

  // 🆕 清理方法
  private cleanupWebSocketHandlers(): void {
    // 移除所有注册的游戏事件监听器（使用同一引用）
    Object.entries(this.boundHandlers).forEach(([evt, fn]) => {
      if (fn) {
        webSocketService.offGameEvent(evt, fn as any);
        this.boundHandlers[evt] = null;
      }
    });
    this.handlersRegistered = false;

    if (this.fallbackTimer) {
      this.fallbackTimer.remove();
      this.fallbackTimer = null;
    }

    this.snapshotAgents.clear();
    this.snapshotResourceIds.clear();

    // 解绑 CombatEventRouter 与 ABR 事件
    try {
      if (this.combatEventRouter && typeof this.combatEventRouter.unbind === 'function') {
        this.combatEventRouter.unbind();
      }
      window.removeEventListener('ABR_COMBAT_STARTED' as any, this.onAbrCombatStarted as any);
      window.removeEventListener('ABR_COMBAT_ENDED' as any, this.onAbrCombatEnded as any);
    } catch {}
    // 清理箭头
    try { this.turnArrowMap.forEach(({ gfx, tween }) => { if (tween) tween.stop(); gfx.destroy(); }); this.turnArrowMap.clear(); } catch {}
  }

  // ==================== 🆕 回合开始高亮箭头 ====================
  private activateAgentHighlight(agentId: string): void {
    if (!agentId) return;
    this.awaitingHighlightAgentId = null;
    try { this.turnArrowMap.forEach(({ gfx, tween }) => { if (tween) tween.stop(); gfx.destroy(); }); this.turnArrowMap.clear(); } catch {}
    this.showAgentTurnArrow(agentId);
    try { this.registry.set('activeAgentId', agentId); } catch {}
    try {
      const auto = !!this.registry.get('autoFollowCamera');
      if (!auto) return;
      const cam = this.cameras?.main; if (!cam) return;
      let container: Phaser.GameObjects.Container | undefined;
      try { container = this.phaserAnimationManager?.agentContainers.get(agentId); } catch {}
      if (!container) { container = this.agents.get(agentId); }
      if (container) {
        cam.startFollow(container, true, 0.2, 0.2);
        cam.setRoundPixels(true);
      } else {
        try {
          const tileSize = (this.registry.get('gridSize') || this.registry.get('tileSize') || 24) as number;
          const tp = (this.phaserAnimationManager as any)?.getAgentTilePosition?.(agentId);
          if (tp && typeof tp.x === 'number' && typeof tp.y === 'number') {
            cam.centerOn(tp.x * tileSize + tileSize / 2, tp.y * tileSize + tileSize / 2);
          }
        } catch {}
        try {
          this.time.delayedCall(80, () => {
            try {
              let c: Phaser.GameObjects.Container | undefined;
              try { c = this.phaserAnimationManager?.agentContainers.get(agentId); } catch {}
              if (!c) { c = this.agents.get(agentId); }
              if (c) {
                cam.startFollow(c, true, 0.2, 0.2);
                cam.setRoundPixels(true);
              }
            } catch {}
          });
        } catch {}
      }
    } catch (e) {
      console.warn('camera follow failed:', (e as Error).message);
    }
  }

  private showAgentTurnArrow(agentId: string): void {
    try {
      // 若已存在，先确保可见并重置动画
      const existing = this.turnArrowMap.get(agentId);
      if (existing) {
        existing.gfx.setVisible(true);
        if (existing.tween) { existing.tween.stop(); }
        existing.tween = this.tweens.add({ targets: existing.gfx, alpha: { from: 1, to: 0.3 }, yoyo: true, repeat: -1, duration: 400 });
        return;
      }

      // 获取agent容器（来自 PhaserAnimationManager）
      const pma: any = this.phaserAnimationManager as any;
      const container: Phaser.GameObjects.Container | undefined = pma?.agentContainers?.get(agentId);
      if (!container) {
        console.warn(`showAgentTurnArrow: agent container not found: ${agentId}`);
        return;
      }

      // 创建箭头图形（警示橙色、朝下三角），添加到agent容器中，位于角色上方
      const g = this.add.graphics();
      g.setDepth((container.depth || 1500) + 10);
      // 使用鲜艳警示橙色
      g.fillStyle(0xFFA500, 1);
      // 在容器局部坐标绘制（相对中心点）- 朝下：tipY > topY
      const topY = -62; const tipY = -46; const halfW = 8;
      g.fillTriangle(-halfW, topY, halfW, topY, 0, tipY);
      g.lineStyle(1, 0x000000, 0.6);
      g.strokeTriangle(-halfW, topY, halfW, topY, 0, tipY);
      g.setAlpha(1);
      container.add(g);

      const tween = this.tweens.add({ targets: g, alpha: { from: 1, to: 0.3 }, yoyo: true, repeat: -1, duration: 400 });
      this.turnArrowMap.set(agentId, { gfx: g, tween });
    } catch (e) {
      console.warn('showAgentTurnArrow failed:', (e as Error).message);
    }
  }

  private hideAgentTurnArrow(agentId: string): void {
    const entry = this.turnArrowMap.get(agentId);
    if (!entry) return;
    try { if (entry.tween) entry.tween.stop(); } catch {}
    try { entry.gfx.destroy(); } catch {}
    this.turnArrowMap.delete(agentId);
  }

  // 👁️ P2.1: Update spectator count display
  public updateSpectatorCount(count: number): void {
    this.currentSpectatorCount = count;
    if (this.spectatorCountText) {
      this.spectatorCountText.setText(`👁️ Spectators: ${count}`);
    }
  }

  // 👁️ P2.1: Get current spectator count
  public getSpectatorCount(): number {
    return this.currentSpectatorCount;
  }

}
